// src/screens/CreateEventsScreen.js
// UPDATED VERSION - Includes H2H mode, crossplay, visibility, team support, proper event naming, and admin custom titles
// 
// FIXES:
// 1. Properly calculates number of entrants based on team sizes from gameData.js
// 2. Fixed date picker functionality for selecting event start time
// 3. Navigates to LobbyScreen after event creation instead of EventDetail
// 4. Added Head-to-Head (H2H) mode support for single-match events
//
// NEW FEATURES:
// - HEAD-TO-HEAD MODE: One match, two sides (solo or teams, any size the
//   format allows) with winner-take-all payouts
// - Platform selection with crossplay support
// - Public/Private visibility toggle
// - Team size selection for multiplayer games
// - 2-bracket and 2-heat format support
// - Base stream title used as event name
// - ADMIN CUSTOM EVENT TITLE INPUT
// - CORRECTED BRACKET SIZE: Now based on number of MATCHES, not participants
// - GAME AVAILABILITY: Admin can enable/disable games from appearing

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  Alert,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

import { db } from '../../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

import { useAdminAuth } from '../hooks/useAdminAuth';
import usePayouts from '../hooks/usePayouts';
import { useGameAvailability } from '../hooks/useGameAvailability';
import useCritsEnabled from '../hooks/useCritsEnabled';

import { GAME_PROFILES, GT7_LOBBY_SETTINGS, GT7_SETTING_SECTION_LABELS, extractLobbyValues, MADDEN_GAME_SETTINGS, maddenDefaultSettings, estimateMaddenDurationMinutes, BR_PLATFORM_MAX_PLAYERS } from '../constants/gameData';
// Open catalog (founder 2026-08-12): every game is creatable — browse-catalog
// games (box art, no full profile) and free-text "Other" games run on the
// generic community profile with player-reported results.
import {
  EXTRA_EVENT_GAMES,
  COMMUNITY_GENRES,
  buildCommunityGameProfile,
  communityGameKeyFromName,
  gameUsesManualResults,
  isKnownCommunityGame,
  normalizeCommunityGameName,
} from '../constants/communityGames';
// structureName is aliased: getEventNaming has a local `structureName`.
import { PAYOUT_STRUCTURES, structureName as structureDisplayName } from '../constants/payoutStructures';


import { COLORS, toCents, fmtUSD } from '../utils/eventHelper';
import { fmtCrits } from '../utils/currencyHelper';
import { handleCreateEvent } from '../utils/eventCreationHelpers';
import { openTermsOfService } from '../utils/openTermsOfService';
import { generateEventNames } from '../utils/eventNamingHelper';
import { useEventEligibility } from '../hooks/useEventEligibility';
import { useError } from '../components/ErrorContext';
import UpgradeMembershipModal from '../components/UpgradeMembershipModal';
import BuyCritsModal from '../components/BuyCritsModal';
import { buildHostRewardTable, getHostTier } from '../constants/hostRewards';
import { Ionicons } from '@expo/vector-icons';



let DatePicker = null;
try {
  DatePicker = require('../utils/DatePicker.web.js').default;
} catch (_) {
  DatePicker = null;
}

// Bracket/heat SIZE LADDER — mirrors functions/constants/eventEconomy.js
// BRACKET_SIZE_LADDER (server-enforced; keep in sync). Sizes above 16 carry a
// Crit unlock fee (the creator's buy-in — counts toward their entry via max()
// and seeds the prize pool) and force a minimum per-entrant Crit entry cost.
// 2-16 are free with creator-chosen entry, as always. Admins pay no fees
// (their creation path skips charges entirely).
const SIZE_LADDER = {
  2: { unlockFee: 0, minEntry: 0 }, 4: { unlockFee: 0, minEntry: 0 },
  8: { unlockFee: 0, minEntry: 0 }, 16: { unlockFee: 0, minEntry: 0 },
  32: { unlockFee: 5, minEntry: 2 }, 64: { unlockFee: 10, minEntry: 3 },
  128: { unlockFee: 20, minEntry: 5 }, 256: { unlockFee: 40, minEntry: 8 },
  512: { unlockFee: 60, minEntry: 10 }, 1024: { unlockFee: 90, minEntry: 15 },
  2048: { unlockFee: 125, minEntry: 20 }, 4096: { unlockFee: 175, minEntry: 25 },
  8192: { unlockFee: 250, minEntry: 30 }, 16384: { unlockFee: 400, minEntry: 40 },
};
const ALL_SIZES = Object.keys(SIZE_LADDER).map(Number).sort((a, b) => a - b);
const sizeTierUI = (size) => SIZE_LADDER[size] || { unlockFee: 0, minEntry: 0 };
const fmtSize = (n) => (n >= 1024 ? `${n / 1024}K` : String(n));

// The paid ladder opened the full size list to everyone (Phase 4b) — the
// unlock fee is the gate now, not the admin flag.
function getBracketSizeOptions() { return ALL_SIZES; }
function getHeatSizeOptions() { return ALL_SIZES; }

// Warzone battle-royale squad-size display labels (squadSize 1-4). Squads are
// IN-GAME groupings only - entries stay per-player (event teamSize is 1).
const SQUAD_SIZE_LABELS = { 1: 'Solos', 2: 'Duos', 3: 'Trios', 4: 'Quads' };

// Warzone BR payout bands - MIRROR of functions/constants/payoutStructures.js
// 'wz-solos-top-10' / 'wz-squads-top-5' (keep in sync). BR showdowns must NOT
// use the entrant-scaled GT7 showdown bands (2-15 clamp), so these static
// bands are fed to usePayouts via the event-override path for the preview.
// Squads: each band is the SQUAD's share at that placement, split evenly
// among the squad's players at payout time.
const WZ_SOLOS_PREVIEW_BANDS = [
  { tier: 'Champion',   minRank: 1,  maxRank: 1,  percent: 30 },
  { tier: '2nd Place',  minRank: 2,  maxRank: 2,  percent: 20 },
  { tier: '3rd Place',  minRank: 3,  maxRank: 3,  percent: 14 },
  { tier: '4th Place',  minRank: 4,  maxRank: 4,  percent: 10 },
  { tier: '5th Place',  minRank: 5,  maxRank: 5,  percent: 8 },
  { tier: '6th Place',  minRank: 6,  maxRank: 6,  percent: 6 },
  { tier: '7th Place',  minRank: 7,  maxRank: 7,  percent: 4 },
  { tier: '8th Place',  minRank: 8,  maxRank: 8,  percent: 3 },
  { tier: '9th Place',  minRank: 9,  maxRank: 9,  percent: 3 },
  { tier: '10th Place', minRank: 10, maxRank: 10, percent: 2 },
];
const WZ_SQUADS_PREVIEW_BANDS = [
  { tier: 'Winning Squad', minRank: 1, maxRank: 1, percent: 40 },
  { tier: '2nd Squad',     minRank: 2, maxRank: 2, percent: 25 },
  { tier: '3rd Squad',     minRank: 3, maxRank: 3, percent: 15 },
  { tier: '4th Squad',     minRank: 4, maxRank: 4, percent: 12 },
  { tier: '5th Squad',     minRank: 5, maxRank: 5, percent: 8 },
];

function Pill({ label, active, onPress, style, disabled }) {
  return (
    <TouchableOpacity 
      onPress={onPress} 
      disabled={disabled}
      style={[styles.pill, active && styles.pillActive, disabled && styles.pillDisabled, style]}
    >
      <Text style={[styles.pillText, active && styles.pillTextActive, disabled && styles.pillTextDisabled]}>{label}</Text>
    </TouchableOpacity>
  );
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const nowMs = () => Date.now();
const addMs = (d, ms) => new Date(d.getTime() + ms);
const ceilLog2 = (n) => Math.max(1, Math.ceil(Math.log2(Math.max(2, n))));

// Inter-round gap steps (minutes) — mirrors functions/constants/eventEconomy.js
// INTER_ROUND_GAP_MINUTES. How long players get between rounds; default 5 (a bio
// break), up to a week for marquee multi-day events people tune in to watch.
const GAP_STEPS = [
  { min: 5, label: '5m' }, { min: 10, label: '10m' }, { min: 15, label: '15m' },
  { min: 30, label: '30m' }, { min: 60, label: '1h' },
  { min: 1440, label: '1d' }, { min: 10080, label: '1w' }, { min: 43200, label: '1mo' },
];
const DEFAULT_GAP_MIN = 5;
// Gaps of a day or more are DAY-ANCHORED: every round starts at round 1's clock
// time (9 AM start -> 9 AM the next day/week/month), no play-time drift.
const DAY_ANCHOR_MIN = 1440;

// Round label for the projected-schedule preview.
const projectedRoundLabel = (round, total) => {
  if (round === total) return 'Final';
  if (round === total - 1) return 'Semifinals';
  if (round === total - 2) return 'Quarterfinals';
  return `Round ${round}`;
};

// Format an absolute time for the projection. Same-day → time only; otherwise
// include the weekday + date so week-long gaps read clearly.
const fmtWhen = (ms) => {
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  const sameDay = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
};

// Human span for "X total".
const fmtSpan = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const hrs = min / 60;
  if (hrs < 48) return `${(hrs).toFixed(hrs < 10 ? 1 : 0).replace(/\.0$/, '')}h`;
  const days = hrs / 24;
  return `${days.toFixed(days < 10 ? 1 : 0).replace(/\.0$/, '')} days`;
};

// Race duration estimation. Base average mph by vehicle category, scaled by
// track type (ovals run hot, rally surfaces run cold) and length (short
// tracks have more corner density, long ones have more straight). Returns
// estimated race minutes for given track + laps + class + pit stops.
const VEHICLE_AVG_MPH = {
  gr1: 115,
  gr2: 108,
  gr3: 100,
  gr4: 88,
  grB: 75,
  no_limit: 100,
};
const TRACK_TYPE_FACTOR = {
  road: 1.0,
  oval: 1.4,
  rally: 0.55,
};
const trackLengthFactor = (mi) => mi < 2 ? 0.7 : mi > 6 ? 1.2 : 1.0;
const PIT_STOP_MINUTES = 0.5;
const formatEnduranceTime = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const ENDURANCE_ROOM_MODE = 'Practice/Qualifier/Endurance Race';
const estimateRaceMinutes = (track, laps, carCategory, pitStops) => {
  if (!track || !laps || !track.lengthMiles) return 0;
  const baseAvg = VEHICLE_AVG_MPH[carCategory] || VEHICLE_AVG_MPH.no_limit;
  const typeFactor = TRACK_TYPE_FACTOR[track.type || 'road'] || 1.0;
  const lenFactor = trackLengthFactor(track.lengthMiles);
  const avgMph = baseAvg * typeFactor * lenFactor;
  const minPerLap = (track.lengthMiles / avgMph) * 60;
  return minPerLap * laps + (pitStops || 0) * PIT_STOP_MINUTES;
};

export default function CreateEventScreen({ navigation }) {
  const { user, isAdmin } = useAdminAuth();
  // Evergreen step 2.3: settings/economy.critsEnabled. Starts TRUE and flips
  // asynchronously, so every Crit value below is DERIVED from it — never
  // captured into a useState initializer. OFF hides the Crit economy (entry
  // cost, fees, pools) and the event posts on the Crit path with cost 0.
  const critsEnabled = useCritsEnabled();
  // Cross-platform error dialog — react-native Alert.alert is a NO-OP on web,
  // so create failures (incl. title-moderation rejections) surfaced nothing.
  const { showError } = useError();

  // In-place upgrade funnel: members-only rejection opens plan-pick + embedded
  // Stripe checkout IN a modal — no navigation away (founder: leaving the
  // filled-out form = abandoned cart).
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Sibling funnel for insufficient-Crits rejections: Crit packs in-place.
  // null = closed; {have, need} or {} = open (shortfall shown when known).
  const [critShortfall, setCritShortfall] = useState(null);

  // Subscription tier — drives the custom-title fee display (Pro/Elite free,
  // Rookie 5). The server recomputes all fees authoritatively at create time.
  const [userTier, setUserTier] = useState('free');
  // Client mirror of functions/modules/subscriptions.js hasPaidMembership:
  // admin role, OR a paid tier with a live Stripe status (past_due keeps
  // access while Stripe retries), OR a yearly entitlement still paid through.
  // Drives the Crits-OFF size gate only (sizes above 16 are for members); the
  // server is authoritative and its rejection opens the same upgrade funnel.
  const [isPaidMember, setIsPaidMember] = useState(false);
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const d = snap.exists() ? (snap.data() || {}) : {};
      setUserTier(d.subscriptionTier || 'free');
      setIsPaidMember(
        d.role === 'admin'
        || (['rookie', 'pro', 'elite'].includes(d.subscriptionTier)
          && ['active', 'trialing', 'past_due'].includes(d.subscriptionStatus))
        || ((d.yearlyEntitlement?.paidThrough?.toMillis?.() || 0) > Date.now())
      );
    }, () => { setUserTier('free'); setIsPaidMember(false); });
    return () => unsub();
  }, [user?.uid]);

  // Per-mode soft-hide (settings/events.hideHeats / .hideTournaments): when a
  // flag is on, non-admins don't see that mode in the picker (backend enforces
  // the same in createEventWithHold). Live-subscribed like Header's
  // adminOnlyEventCreation read so an admin flip applies without a reload.
  const [hiddenModes, setHiddenModes] = useState({ heats: false, tournaments: false });
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'events'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setHiddenModes({ heats: !!d.hideHeats, tournaments: !!d.hideTournaments });
    }, () => setHiddenModes({ heats: false, tournaments: false }));
    return () => unsub();
  }, []);

  // Genre categories host their open-catalog games too (Mario Kart World
  // rides with GT7 under Racing); 'rts' (Strategy) exists only for extras;
  // 'community' = More Games (genre-'other' extras + free-text "Other").
  const categories = ['fighting', 'racing', 'sports', 'moba', 'shooter', 'rts', 'community'];
  const gameKeys = useMemo(() => Object.keys(GAME_PROFILES), []);
  const firstGameForCat = (cat) =>
    gameKeys.find((k) => (GAME_PROFILES[k]?.type || '') === cat) || gameKeys[0];
  // Open-catalog games that belong under a genre category pill.
  const extrasForCat = useCallback((cat) => Object.keys(EXTRA_EVENT_GAMES)
    .filter((k) => (EXTRA_EVENT_GAMES[k]?.genre || 'other') === cat), []);

  // Default category/game: racing/gt7 — GT7 is CritCash's primary
  // creator-led growth game, and the previous fighting/SF6 default was
  // causing rushed users to accidentally publish Street Fighter events.
  const [category, setCategory] = useState('racing');
  const [game, setGame] = useState(firstGameForCat('racing'));
  // Free-text game name + declared genre — only used while game === 'other'.
  const [customGameName, setCustomGameName] = useState('');
  const [otherGenre, setOtherGenre] = useState('other');
  const communityName = normalizeCommunityGameName(customGameName);
  // The key the backend will store ('' while an "Other" name is still empty).
  const effectiveGameKey = game === 'other'
    ? (communityGameKeyFromName(communityName) || '')
    : game;
  // Player-reported results: every game outside the vision list settles by
  // the players' own reports (backend stamps manualResults the same way).
  // 'other' counts even before a name is typed — every cg_* game reports.
  // Heats (mode 'racing') are the exception: sub-matches are admin-resolved.
  const usesManualResults = game === 'other' || gameUsesManualResults(effectiveGameKey);
  // Typed name matched the community game catalog (or a browse-catalog
  // extra): name + genre are canonical, the creator's genre pick is moot.
  const isCatalogMatch = game === 'other' && !!communityName && isKnownCommunityGame(effectiveGameKey);

  // Keep the local genre pick in sync with a catalog match so the payload
  // and genre-dependent UI agree with what the server stores.
  useEffect(() => {
    if (isCatalogMatch && gp?.genre && gp.genre !== otherGenre) {
      setOtherGenre(gp.genre);
    }
  }, [isCatalogMatch, gp?.genre]); // eslint-disable-line react-hooks/exhaustive-deps
  const gp = GAME_PROFILES[game]
    || (game
      ? buildCommunityGameProfile(
        effectiveGameKey || game,
        game === 'other' ? (communityName || null) : null,
        game === 'other' ? otherGenre : undefined,
      )
      : {});
  
  // Platform selection state (hoisted above the eligibility hook — platform-
  // locked events require the creator to have an IGN for that platform).
  // Default matches resetForGame: Cross-Platform when the game offers it
  // (open event), otherwise the game's first platform.
  const [selectedPlatform, setSelectedPlatform] = useState(() => {
    const ap = GAME_PROFILES[firstGameForCat('racing')]?.allowedPlatforms || [];
    return ap.find((p) => String(p).toLowerCase() === 'cross-platform') || ap[0] || '';
  });

  // Check if user is eligible to create events (has IGN + stream key).
  // Non-admin creators play in their own events, so a platform-locked event
  // requires their IGN on that platform. Admins create official events on
  // behalf of the field and are exempt (matches backend createEventWithHold).
  const {
    isEligible,
    isLoading: eligibilityLoading,
    missingRequirements,
    hasIGN,
    hasAnyIGN,
    ign,
    hasStreamKey,
    serviceAccountName,
    serviceShortName,
  } = useEventEligibility(user?.uid, effectiveGameKey || null, isAdmin ? null : (selectedPlatform || null));

  // GAME AVAILABILITY: Fetch which games are enabled/disabled
  const { 
    isGameEnabled, 
    loading: availabilityLoading,
  } = useGameAvailability();

  // Filter available game keys - everyone only sees enabled games
  const availableGameKeys = useMemo(() => {
    return gameKeys.filter(isGameEnabled);
  }, [gameKeys, isGameEnabled]);

  // Helper to find first available game in a category
  const firstAvailableGameForCat = useCallback((cat) => {
    if (cat === 'community') return extrasForCat('other')[0] || 'other';
    return availableGameKeys.find((k) => (GAME_PROFILES[k]?.type || '') === cat)
      || extrasForCat(cat)[0]
      || availableGameKeys[0];
  }, [availableGameKeys, extrasForCat]);

  // Check if current game is still available (for when admin disables it)
  useEffect(() => {
    if (!availabilityLoading && !isAdmin && !isGameEnabled(game)) {
      // Current game was disabled - switch to first available in category
      const newGame = firstAvailableGameForCat(category);
      if (newGame && newGame !== game) {
        setGame(newGame);
        resetForGame(newGame, category);
      }
    }
  }, [game, category, isAdmin, isGameEnabled, availabilityLoading]);

  // NEW: Mode selection state - determines if event is H2H, Racing, Showdown, or Bracket
  // Initialize from the default game's allowedModes so the mode pill matches
  // the auto-selected game on first render (GT7 → 'racing').
  const [selectedMode, setSelectedMode] = useState(
    () => (GAME_PROFILES[firstGameForCat('racing')]?.allowedModes?.[0]) || 'bracket'
  );
  
  const isRacing = selectedMode === 'racing';
  const isShowdown = selectedMode === 'showdown';
  const isH2H = selectedMode === 'h2h';
  const isBracket = selectedMode === 'bracket';
  
  // Legacy formats (e.g. Madden's old quarter-length presets) stay in gameData
  // so existing event docs resolve metadata, but are hidden from the create UI.
  // Mode-aware for non-racing games: showdown = battle-royale formats only
  // (Warzone playlists), bracket/h2h = everything else. Racing games keep the
  // full list - their formats work in every mode.
  const formatOptions = useMemo(() => {
    const keys = Object.keys(gp?.formats || {}).filter((k) => !gp.formats[k]?.legacy);
    if (gp?.type === 'racing') return keys;
    if (isShowdown) {
      const br = keys.filter((k) => gp.formats[k]?.battleRoyale);
      return br.length ? br : keys;
    }
    return keys.filter((k) => !gp.formats[k]?.battleRoyale);
  }, [gp, isShowdown]);
  const [formatName, setFormatName] = useState(formatOptions[0] || 'Standard');

  // Warzone battle royale - showdown-chassis single-lobby FFA events. The
  // format carries the lobby cap (maxPlayers), the game's start floor
  // (minPlayers) and the allowed in-game squad sizes (teamSizes 1-4).
  const selectedFormatData = gp?.formats?.[formatName];
  const isBattleRoyaleFormat = !!selectedFormatData?.battleRoyale;
  const isBattleRoyale = isShowdown && isBattleRoyaleFormat;
  const brMinPlayers = Math.max(2, Math.floor(Number(selectedFormatData?.minPlayers)) || 2);

  // Keep the selected format inside the mode-filtered list (e.g. switching
  // cod bracket -> Battle Royale snaps CDL Hardpoint to the first BR playlist,
  // and back again the other way).
  useEffect(() => {
    if (formatOptions.length && !formatOptions.includes(formatName)) {
      setFormatName(formatOptions[0]);
    }
  }, [formatOptions, formatName]);

  // Host overrides on top of the format preset's laps / pits / tire defaults.
  // When the host picks a different format these reset to that format's values.
  const [lapsOverride, setLapsOverride] = useState(5);
  const [pitStopsOverride, setPitStopsOverride] = useState(0);
  const [tireChangeOverride, setTireChangeOverride] = useState(false);
  const [enduranceMinutes, setEnduranceMinutes] = useState(60);
  useEffect(() => {
    const fmt = gp?.formats?.[formatName];
    if (fmt) {
      if (typeof fmt.laps === 'number') setLapsOverride(fmt.laps);
      if (typeof fmt.minPitStops === 'number') setPitStopsOverride(fmt.minPitStops);
      if (typeof fmt.requiredTireTypeChange === 'boolean') setTireChangeOverride(fmt.requiredTireTypeChange);
    }
  }, [formatName, gp]);

  // Post-pivot (2026-06-26): events are entered with Crits (non-cash play
  // currency), not a $ entry fee. entryFeeCents is held at 0 so the backend
  // routes to the Crit entry path (isCritEntryEvent); critEntryCost is what
  // participants spend to enter.
  const [critCostInput, setCritCostInput] = useState('5');
  // Crits OFF: entry is 0 (derived, not a useState initializer — the flag
  // resolves after mount and a rollback flip restores the typed value).
  const critEntryCost = critsEnabled ? Math.max(0, Math.floor(Number(critCostInput) || 0)) : 0;
  const entryFeeCents = 0;

  // Inter-round gap (minutes). Only meaningful for multi-round events; sent to
  // the backend as roundScheduleMode:'gap' + interRoundGapMinutes.
  const [interRoundGapMinutes, setInterRoundGapMinutes] = useState(DEFAULT_GAP_MIN);

  // NEW: Visibility state
  const [visibility, setVisibility] = useState('public');

  // Host vs Racer role for games that support a dedicated host (GT7)
  const [creatorRole, setCreatorRole] = useState('racer');

  // Host & race / Host & play: a host may ALSO compete. GT7: they take the
  // 16th lobby seat (full grid) instead of spectating (15 racers + host).
  // Battle royale (founder 2026-09-02, generalizing the GT7 opt-in): the
  // creator-host drops in too, taking a NORMAL player slot inside the format
  // cap — no extra seat, Warzone lobby caps are physical. The spectate-only
  // CODCaster seat stays the dedicated-host mode.
  const [hostRaces, setHostRaces] = useState(false);

  // NEW: Team size state - initialized from format
  const [teamSize, setTeamSize] = useState(1);

  // Warzone BR: in-game squad size (1=Solos, 2=Duos, 3=Trios, 4=Quads).
  // Entries stay per-player (event teamSize is forced to 1 for showdown) -
  // squads form in the game lobby. Stamped on the event as ev.squadSize.
  const [squadSize, setSquadSize] = useState(4);

  // Default the squad size to the BR format's default whenever the format
  // changes; the format's teamSizes are the allowed squad sizes.
  useEffect(() => {
    const fmt = gp?.formats?.[formatName];
    if (!fmt?.battleRoyale) return;
    const sizes = Array.isArray(fmt.teamSizes) && fmt.teamSizes.length ? fmt.teamSizes : [1];
    setSquadSize(sizes.includes(fmt.defaultTeamSize) ? fmt.defaultTeamSize : sizes[0]);
  }, [formatName, gp]);

  // COD host model: which bracket rounds the creator will personally host
  // (one match per round). Only used when the creator picks the host role.
  const [hostSelectedRounds, setHostSelectedRounds] = useState([]);

  // COD/CDL: a non-playing host (CODCaster) is needed for team events at or
  // above hostMinTeamSize (3v3+). 1v1 and 2v2 run without a host. The creator
  // CHOOSES host or participant (Your Role panel below) — not forced.
  // hostEligible just means "the host option applies at this team size."
  // Battle-royale (Warzone) events ALWAYS take a dedicated host regardless of
  // squad size - the CODCaster spectates and never consumes a player slot.
  const hostEligible = !!gp?.requiresHost
    && (isBattleRoyaleFormat ? true : teamSize >= (gp?.hostMinTeamSize ?? 1))
    && game !== 'mdn';

  // Madden ('mdn') is a direct EA-IGN invite system, never a hosted event.
  // Dedicated-host mode = GT7 racing/showdown (optional host) OR a COD team
  // event (3v3+) where the creator chose to host.
  const isHostMode = gp?.supportsHost
    && (isRacing || isShowdown || hostEligible)
    && creatorRole === 'host'
    && game !== 'mdn';

  // NEW: Admin custom event title
  const [customEventTitle, setCustomEventTitle] = useState('');
  const [useCustomTitle, setUseCustomTitle] = useState(false);

  // GT7-specific state
  const [selectedCarCategory, setSelectedCarCategory] = useState(
    gp?.roomSettings?.common?.carCategory?.default || 'gr3'
  );
  const [selectedTracks, setSelectedTracks] = useState([]); // Array of track name strings
  const [trackDropdownOpen, setTrackDropdownOpen] = useState(-1); // Which track dropdown is open (-1 = none)
  const [trackSearch, setTrackSearch] = useState('');

  // GT7 Advanced Settings — host overrides on top of GT7_LOBBY_SETTINGS defaults.
  // Shape: { [sectionKey]: { [settingKey]: 'overridden value', ... }, ... }
  // Empty object = use all defaults. Only changed settings live here.
  const [lobbySettingsOverrides, setLobbySettingsOverrides] = useState({});
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  // Which section subpanels are expanded inside the advanced panel
  const [advancedSectionOpen, setAdvancedSectionOpen] = useState({});
  // Which setting dropdown is currently open (only one at a time).
  // Empty string = none open. Key format: `${sectionKey}.${settingKey}`.
  const [openSettingDropdown, setOpenSettingDropdown] = useState('');

  // Keep details.maxParticipants in sync with the event mode. The GT7 lobby
  // physically allows up to 16 slots but Head-to-Head only fills 2; locking
  // this prevents hosts from advertising slot counts the mode can't honor.
  useEffect(() => {
    if (game !== 'gt7') return;
    const value = isH2H ? '2' : '16 (Host occupies one slot)';
    setLobbySettingsOverrides(prev => ({
      ...prev,
      details: { ...(prev.details || {}), maxParticipants: value },
    }));
  }, [game, isH2H]);

  // Wipe overrides when the game changes — overrides are GT7-specific.
  useEffect(() => {
    if (game !== 'gt7') {
      setLobbySettingsOverrides({});
      setAdvancedSettingsOpen(false);
      setAdvancedSectionOpen({});
    }
  }, [game]);

  // Madden in-game settings (skill, quarter length, accelerated clock,
  // injuries, even teams, game style). Stored flat on the event doc as
  // ev.gameSettings; reset to schema defaults when the game changes.
  const [maddenSettings, setMaddenSettings] = useState(() => maddenDefaultSettings());
  useEffect(() => {
    setMaddenSettings(maddenDefaultSettings());
  }, [game]);
  const setMaddenSetting = (key, value) =>
    setMaddenSettings((prev) => ({ ...prev, [key]: value }));

  // Default the role to host when COD's host option becomes available (3v3+) so
  // the event has a host by default — but the creator can switch to Participant.
  // The effect only re-fires when eligibility flips, so it preserves the user's
  // toggle within a size tier. Below the threshold / non-host games → player.
  useEffect(() => {
    setCreatorRole(hostEligible ? 'host' : 'racer');
  }, [hostEligible]);

  // True when the host has selected the Endurance Race room mode — the laps
  // slider swaps for a duration slider (up to 24h) in that case.
  const isEndurance = useMemo(() => {
    const mode = lobbySettingsOverrides?.details?.roomMode
      ?? GT7_LOBBY_SETTINGS?.details?.roomMode?.value;
    return mode === ENDURANCE_ROOM_MODE;
  }, [lobbySettingsOverrides]);

  // NEW: User balance state for payment info display
  const [balanceCents, setBalanceCents] = useState(0);
  const [giftBalanceCents, setGiftBalanceCents] = useState(0);

  // Host profile — for earnings estimate tier calculation
  const [hostProfile, setHostProfile] = useState(null);

  // Fetch user balances + host profile
  useEffect(() => {
    if (!user?.uid) return;
    const unsubBalance = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setBalanceCents(data.balanceCents || 0);
          setGiftBalanceCents(data.giftBalanceCents || 0);
        }
      },
      (err) => console.warn('Balance fetch error:', err)
    );
    const unsubHost = onSnapshot(
      doc(db, 'hosts', user.uid),
      (snap) => setHostProfile(snap.exists() ? snap.data() : null),
      () => setHostProfile(null)
    );
    return () => { unsubBalance(); unsubHost(); };
  }, [user?.uid]);
  
  // Combined balance for display
  const totalBalanceCents = balanceCents + giftBalanceCents;
  
  // Create confirmation modal state
  const [showCreateConfirmModal, setShowCreateConfirmModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const bracketSizeOptions = useMemo(() => getBracketSizeOptions(isAdmin), [isAdmin]);
  const heatSizeOptions = useMemo(() => getHeatSizeOptions(isAdmin), [isAdmin]);

  const defaultBracketSize = isAdmin ? 32 : 16; // Teams, not matches
  const defaultHeatCount = isAdmin ? 16 : 8;
  // Format-aware grid. Warzone BR formats carry their own lobby cap: the
  // squad-size-tuned cap when the format defines one (Resurgence Quads 44),
  // else the format's best-case maxPlayers (Verdansk 150) - clamped to
  // BR_PLATFORM_MAX_PLAYERS while scoring is manual (mirrors the server's
  // effective-cap chain in events.js). Everything else keeps the format /
  // game-profile grid (GT7 15).
  const brSizeCap = Number(selectedFormatData?.maxPlayersBySquadSize?.[squadSize])
    || Number(selectedFormatData?.maxPlayers) || 0;
  const brCapClamped = isBattleRoyaleFormat && brSizeCap > BR_PLATFORM_MAX_PLAYERS;
  const gridSize = isBattleRoyaleFormat
    ? Math.min(brSizeCap || BR_PLATFORM_MAX_PLAYERS, BR_PLATFORM_MAX_PLAYERS)
    : (Number(gp?.formats?.[formatName]?.maxPlayers) || gp.maxPlayers || 15);

  // "Host & race" — the host takes the lobby seat normally reserved for a
  // spectating host, so the grid grows by one (GT7: 15 -> 16 full grid).
  // Applies to BOTH showdowns and heats (founder 2026-08-20: "16 racers,
  // top 7"). Advancement is NOT affected — advanceCount is capped at
  // floor(payoutStructure.gridSize / 2) = 7 from the STRUCTURE, so a 16-racer
  // heat still advances 7. That is deliberate: round 1 runs a full 16-seat
  // lobby, and every later round carries 2 x 7 = 14 racers, leaving a free
  // seat so a non-racing host can always step in from round 2 onward.
  // Battle royale Host & play (founder 2026-09-02): allowed, but the host
  // takes a normal slot INSIDE the format cap — never grow a BR grid, the
  // in-game lobby cap is physical (Resurgence Quads 44 / clamp 64).
  const hostRacesActive = (isShowdown || isRacing) && isHostMode && hostRaces
    && (gp?.type === 'racing' || isBattleRoyaleFormat);
  const effectiveGridSize = hostRacesActive && !isBattleRoyaleFormat ? gridSize + 1 : gridSize;

  // bracketSize = number of TEAMS in the bracket (standard terminology)
  const [bracketSize, setBracketSize] = useState(defaultBracketSize);
  const [heatCount, setHeatCount] = useState(defaultHeatCount);

  // COD host model: the bracket's rounds + how many matches each has, so the
  // creator (if hosting) can pick which rounds they'll host — one match each.
  // H2H is a single-match event: one round, one match, no picking (the
  // selection effect below auto-selects it so joinEventAsHost gets round 1).
  const hostRoundsList = useMemo(() => {
    if (isH2H) return [{ round: 1, matches: 1, label: 'Final' }];
    if (!isBracket || !bracketSize || bracketSize < 2) return [];
    const totalRounds = Math.max(1, Math.ceil(Math.log2(bracketSize)));
    const rounds = [];
    for (let r = 1; r <= totalRounds; r++) {
      const matches = Math.max(1, Math.floor(bracketSize / Math.pow(2, r)));
      const label = r === totalRounds ? 'Final'
        : r === totalRounds - 1 ? 'Semifinals'
        : `Round ${r}`;
      rounds.push({ round: r, matches, label });
    }
    return rounds;
  }, [isBracket, isH2H, bracketSize]);

  const toggleHostRound = useCallback((r) => {
    setHostSelectedRounds(prev =>
      prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r].sort((a, b) => a - b)
    );
  }, []);

  // Default the host's round selection to ALL rounds while they're hosting, and
  // clear it otherwise. Re-defaults when the bracket size changes the round set.
  useEffect(() => {
    if (isHostMode && hostRoundsList.length > 0) {
      setHostSelectedRounds(hostRoundsList.map(r => r.round));
    } else {
      setHostSelectedRounds([]);
    }
  }, [isHostMode, hostRoundsList]);

  // Calculate total participants based on mode
  // For H2H: always 2 entries (two SIDES - solo players or teams of teamSize)
  // For brackets: participants = bracketSize (number of teams)
  // For racing: participants = gridSize * heatCount
  // For showdown: participants = gridSize (single race)
  const entriesExpected = isH2H
    ? 2
    : isShowdown
      ? effectiveGridSize  // Single race; +1 seat when the host races too
      : isRacing
        ? effectiveGridSize * heatCount  // +1 seat per heat when the host races
        : bracketSize; // Number of teams in bracket

  // Total individual players (for display purposes). Team H2H is 2 sides x
  // teamSize players (solo h2h: 2 x 1 = 2, unchanged).
  const totalPlayers = (isRacing || isShowdown) ? entriesExpected : entriesExpected * teamSize;

  // Initialize platform when game changes
  useEffect(() => {
    if (gp?.allowedPlatforms?.length > 0) {
      setSelectedPlatform(gp.allowedPlatforms[0]);
    }
  }, [game]);

  // Keep the platform ON the resolved game's list. Typing an "Other" name
  // can swap the profile without changing `game` (the community catalog
  // resolves "mario kart" to the Switch-only Mario Kart World), and the
  // server rejects an off-list platform — so re-pick the same way
  // resetForGame does: Cross-Platform when offered, else the first.
  // Mirrors the mode self-heal below.
  // (keyed on a joined string — gp is rebuilt each render, so the array
  // identity alone would re-run this every pass.)
  const allowedPlatformsKey = (gp?.allowedPlatforms || []).join('|');
  useEffect(() => {
    const platforms = gp?.allowedPlatforms || [];
    if (platforms.length === 0 || platforms.includes(selectedPlatform)) return;
    const cross = platforms.find((p) => String(p).toLowerCase() === 'cross-platform');
    setSelectedPlatform(cross || platforms[0]);
  }, [allowedPlatformsKey, selectedPlatform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize mode when game changes - default to first available mode
  useEffect(() => {
    const allowedModes = gp?.allowedModes || ['bracket'];
    if (!allowedModes.includes(selectedMode)) {
      setSelectedMode(allowedModes[0] || 'bracket');
    }
  }, [game]);

  // Get available team sizes for current format
  const availableTeamSizes = useMemo(() => {
    // Racing and Showdown are per-player entries (teamSize 1). H2H is TWO
    // SIDES of whatever size the format allows - not inherently 1v1 (founder
    // 2026-07-20). Formats that carry teamSizes [1] (GT7, Madden, fighting
    // games) stay solo automatically; CDL formats open 6v6..2v2 h2h.
    if (isRacing || isShowdown) return [1];

    const format = gp?.formats?.[formatName];
    if (!format || !format.teamSizes || format.teamSizes.length === 0) {
      return [1]; // Solo only if not specified
    }
    return format.teamSizes;
  }, [gp, formatName, isRacing, isShowdown]);

  // Reset team size when format changes to the default for that format
  useEffect(() => {
    if (isRacing || isShowdown) {
      setTeamSize(1); // Racing and Showdown are per-player entries
      return;
    }

    const format = gp?.formats?.[formatName];
    if (format && format.defaultTeamSize) {
      setTeamSize(format.defaultTeamSize);
    } else if (availableTeamSizes.length > 0) {
      setTeamSize(availableTeamSizes[0]);
    }
  }, [formatName, gp, isRacing, isShowdown]);

  // Ensure team size is valid for current format
  useEffect(() => {
    if (!availableTeamSizes.includes(teamSize)) {
      setTeamSize(availableTeamSizes[0] || 1);
    }
  }, [availableTeamSizes, teamSize]);

  const resetForGame = (gameKey, cat) => {
    const gpx = GAME_PROFILES[gameKey] || buildCommunityGameProfile(gameKey);
    if (gameKey !== 'other') setCustomGameName('');
    const firstFormat = Object.keys(gpx?.formats || {}).filter((k) => !gpx.formats[k]?.legacy)[0] || 'Standard';
    setFormatName(firstFormat);
    setCreatorRole('racer'); // Reset role when switching games
    setHostRaces(false); // Sticky Host & race must not leak into another game's create

    // Reset platform. Prefer Cross-Platform (open event) when the game offers
    // it — platform-locked events should be a deliberate creator choice, since
    // they gate entry on having an IGN for that platform.
    if (gpx?.allowedPlatforms?.length > 0) {
      const cross = gpx.allowedPlatforms.find((p) => String(p).toLowerCase() === 'cross-platform');
      setSelectedPlatform(cross || gpx.allowedPlatforms[0]);
    }

    // Reset to first allowed mode for the game
    const allowedModes = gpx?.allowedModes || ['bracket'];
    setSelectedMode(allowedModes[0]);

    // Reset team size to default for the new format
    const newFormat = gpx?.formats?.[firstFormat];
    const defaultSize = newFormat?.defaultTeamSize || (newFormat?.teamSizes?.[0]) || 1;
    setTeamSize(defaultSize);

    // Reset size options based on mode
    if (allowedModes[0] === 'racing') {
      const defaultHeats = isAdmin ? 16 : 8;
      setHeatCount(defaultHeats);
      setBracketSize(0);
    } else {
      const defaultSize = isAdmin ? 16 : 8;
      setBracketSize(defaultSize);
      setHeatCount(0);
    }

    // Reset GT7-specific state
    setSelectedCarCategory(gpx?.roomSettings?.common?.carCategory?.default || 'gr3');
    setSelectedTracks([]);
  };

  const maxWindowDays = isAdmin ? 30 : 1;
  // Absolute mode minimum lead time: 60 min. Server enforces too (events.js).
  const minStart = addMs(new Date(nowMs()), 60 * 60 * 1000);
  const maxStart = addMs(new Date(nowMs()), maxWindowDays * 24 * 60 * 60 * 1000);

  // Start modes: 'relative' (N min after create), 'absolute' (clock time),
  // 'fill' (starts N min after the event fills to capacity).
  const [startMode, setStartMode] = useState('relative');
  const [startInMins, setStartInMins] = useState(30);
  const [absDate, setAbsDate] = useState(minStart);
  const [fillDelayMins, setFillDelayMins] = useState(15);
  // Optional H2H+GT7 fast path: creator pre-submits the GT7 Room ID so the
  // match seeds with the lobby already known. Empty string = use the
  // standard HostRoomIdModal flow at seed time instead.
  const [creatorRoomId, setCreatorRoomId] = useState('');

  const mode = selectedMode; // Use the selected mode directly
  const entryUnit = teamSize > 1 ? 'team' : 'player';
  const entrants = entriesExpected;

  // Determine crossplay based on platform
  const platformStr = selectedPlatform || 'Cross-Platform';
  const crossplay = platformStr.toLowerCase() === 'cross-platform';

  // Calculate bracket metadata for display
  const roundsToFinal = isBracket ? Math.max(0, ceilLog2(bracketSize) - 1) : isH2H ? 1 : 0;
  const roundsToChampion = isBracket ? ceilLog2(bracketSize) : isH2H ? 1 : 0;
  
  // Calculate heat rounds for racing (elimination rounds + finals)
  // For heats: log2(heatCount) elimination rounds + 1 finals round
  const heatRoundsToFinal = isRacing ? ceilLog2(heatCount) : 0;
  const heatRoundsToChampion = isRacing ? heatRoundsToFinal + 1 : 0;

  // Total rounds for track selection (H2H/Showdown = 1, Heats = heatRoundsToChampion)
  const totalTrackRounds = isRacing ? heatRoundsToChampion : 1;

  // Trim selectedTracks when round count decreases
  useEffect(() => {
    if (selectedTracks.length > totalTrackRounds) {
      setSelectedTracks(prev => prev.slice(0, totalTrackRounds));
    }
  }, [totalTrackRounds]);

  // ── Inter-round gap + projected schedule ───────────────────────────────────
  // Rounds this event will actually run (racing = heat rounds incl. finals;
  // bracket = rounds to champion; H2H/Showdown = 1). The gap selector + timeline
  // only appear when there's more than one round.
  const projectedRounds = isRacing ? heatRoundsToChampion : isBracket ? roundsToChampion : 1;
  const isMultiRound = projectedRounds >= 2;

  // Best-effort per-round play time (minutes). Lap racing uses the same track
  // estimator shown above; endurance/other games fall back to a rough default.
  const perRoundMinutes = (() => {
    if (isRacing && !isEndurance) {
      const t = selectedTracks[0] && gp.tracks?.find((x) => x.name === selectedTracks[0]);
      if (t) return estimateRaceMinutes(t, lapsOverride, selectedCarCategory, pitStopsOverride);
    }
    return 8; // rough default when no lap estimate is available
  })();

  // Round-1 start (ms) from the chosen start mode. Fill mode is unknown until
  // the event fills, so the timeline is shown as unavailable there.
  const startBaseMs = startMode === 'relative'
    ? Date.now() + (Number(startInMins) || 0) * 60000
    : startMode === 'absolute' && absDate
      ? new Date(absDate).getTime()
      : null;

  // Projected per-round start times + estimated end, using the gap the creator
  // picked. These are estimates — actual round starts key off when each round's
  // matches finish (reactive gap), but this educates the creator on the shape,
  // especially the total span for long gaps.
  const projectedSchedule = [];
  let projectedEndMs = null;
  const isDayAnchored = interRoundGapMinutes >= DAY_ANCHOR_MIN;
  if (startBaseMs && isMultiRound) {
    for (let r = 1; r <= projectedRounds; r++) {
      // Day-anchored: fixed grid at round 1's clock time (no play-time drift).
      // Sub-day: each round follows the previous round's play + gap.
      const offsetMin = isDayAnchored
        ? (r - 1) * interRoundGapMinutes
        : (r - 1) * (perRoundMinutes + interRoundGapMinutes);
      projectedSchedule.push({ round: r, startMs: startBaseMs + offsetMin * 60000 });
    }
    projectedEndMs = projectedSchedule[projectedSchedule.length - 1].startMs
      + perRoundMinutes * 60000;
  }

  // Total-span label. Day-anchored events read as INCLUSIVE calendar days —
  // a 15-round daily event spanning Jul 18 -> Aug 1 is "15 days", not 14.
  const projectedSpanLabel = !projectedEndMs ? '—'
    : isDayAnchored
      ? `${Math.floor((projectedSchedule[projectedSchedule.length - 1].startMs - startBaseMs) / 86400000) + 1} days`
      : fmtSpan(projectedEndMs - startBaseMs);

  // Calculate first place prize for proper naming
  const calculateFirstPlacePrize = () => {
    // Calculate gross and net pool
    const grossPool = critEntryCost * (isH2H ? 2 : entriesExpected); // 1 Crit = $1
    const rake = Math.round(grossPool * 0.10); // 10% burn (Crit sink)
    const netPool = grossPool - rake;
    
    // For H2H, winner takes all (100%)
    if (isH2H) {
      return netPool;
    }
    
    // For brackets, get the first place percentage from payout structure
    if (isBracket && entriesExpected > 0) {
      // Use the live payout hook data which already calculated this
      if (live?.stageBands?.length > 0) {
        const firstPlaceBand = live.stageBands[0];
        if (firstPlaceBand && firstPlaceBand.totalCents) {
          return firstPlaceBand.totalCents;
        }
      }
      
      // Fallback: use PAYOUT_STRUCTURES directly
      // Handle the 1k, 2k, 4k, 8k, 16k naming convention
      let psId;
      const size = entriesExpected;
      if (size >= 16384) psId = 'bracket-16k-team';
      else if (size >= 8192) psId = 'bracket-8k-team';
      else if (size >= 4096) psId = 'bracket-4k-team';
      else if (size >= 2048) psId = 'bracket-2k-team';
      else if (size >= 1024) psId = 'bracket-1k-team';
      else psId = `bracket-${size}-team`;
      
      const structure = PAYOUT_STRUCTURES[psId];
      if (structure?.bands?.length > 0) {
        const firstBand = structure.bands[0];
        const firstPercent = firstBand.percent || 100;
        return Math.round(netPool * (firstPercent / 100));
      }
    }
    
    // For racing or unknown, use entire net pool as fallback
    return netPool;
  };

  // Generate proper event and streaming names using new helper
  const getEventNaming = () => {
    // Format prize with k suffix for large amounts (matches bracket formatting)
    // Crit-denominated (1 Crit = $1). Input is whole Crits, not cents.
    const formatPrize = (units) => {
      const n = Math.max(0, Math.floor(Number(units || 0)));
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K Crits`;
      return `${n} Crits`;
    };
    
    // If admin is using custom title, use that for both
    if (useCustomTitle && customEventTitle.trim()) {
      return {
        eventName: customEventTitle.trim(),
        baseStreamTitle: customEventTitle.trim(),
      };
    }

    // For H2H, use a simpler name since there's no bracket size
    // PUBLISHER COMPLIANCE: Event name must NOT include game name
    // Game name is displayed separately in UI via GAME_PROFILES[game].displayName
    if (isH2H) {
      const firstPlacePrizeCents = calculateFirstPlacePrize();
      const gameAbbr = gp?.abbr || gp?.displayName || game;
      const prizeText = firstPlacePrizeCents > 0 
        ? `${formatPrize(firstPlacePrizeCents)} to Winner`
        : '';
      
      return {
        // Event name: NO game name (e.g., "H2H $18 to Winner")
        eventName: prizeText ? `H2H ${prizeText}` : 'H2H Match',
        // Stream title: CAN include game abbrev for Twitch/YouTube categorization
        baseStreamTitle: `CritCash ${gameAbbr} H2H ${prizeText}`.trim(),
      };
    }

    // For brackets, use the proper naming scheme
    if (isBracket) {
      const firstPlacePrizeCents = calculateFirstPlacePrize();
      
      const naming = generateEventNames({
        gameKey: game,
        bracketSize: entriesExpected, // This is the number of entrants
        // Crit-denominated (1 Crit = $1): calculateFirstPlacePrize() returns whole
        // Crits, so the embedded prize must format as Crits, not $.
        firstPlacePrizeCents,
        isCrits: true,
      });

      if (naming) {
        return naming;
      }
    }

    // Fallback for racing, showdown, or if generateEventNames fails
    const gameDisplayName = gp?.displayName || game;
    const gameAbbr = gp?.abbr || gameDisplayName;
    
    // For racing and showdown, get the structure name and calculate first place prize
    if (isRacing || isShowdown) {
      // Warzone battle royale: name from the playlist + squad size. The wz-*
      // payout structure is resolved from squadSize in eventCreationHelpers
      // (and again server-side), so no racing-structure lookup here.
      // PUBLISHER COMPLIANCE: playlist names, no game name in the event name.
      if (isBattleRoyale) {
        const squadLabel = SQUAD_SIZE_LABELS[squadSize] || 'Solos';
        return {
          eventName: `${formatName} - ${squadLabel}`,
          baseStreamTitle: `CritCash ${gameAbbr} ${formatName} - ${squadLabel}`,
        };
      }

      // Build the payout structure ID for racing/showdown
      // Game keys in payout structures: GT7 (uppercase), fzh (lowercase), mkw (lowercase)
      const gameCodeMap = {
        'gt7': 'GT7',
        'fzh': 'fzh',
        'mkw': 'mkw',
        'irc': 'irc',  // iRacing uses lowercase in structure IDs
        'f25': 'f25',  // F1 25 uses lowercase in structure IDs
      };
      const gameKey = gameCodeMap[game.toLowerCase()] || game.toUpperCase();
      
      let psId;
      
      if (isShowdown) {
        // Showdown has specific structure IDs based on game and grid size
        // Format: racing-{game}-top-{half-grid}
        // GT7: 16 grid -> top-8, Forza: 24 grid -> top-12, MKW: 12 grid -> top-6
        const topCount = Math.floor(gridSize / 2);
        psId = `racing-${gameKey}-top-${topCount}`;
      } else {
        // CRITICAL: Use heat count as the size key for multi-heat racing, NOT total entrants
        // racing-fzh-16 means 16 HEATS, not 16 players
        // racing-mkw-8 means 8 HEATS, not 8 players
        // Large heat counts use 'k' suffix: 16k, 8k, 4k, 2k, 1k
        let sizeKey;
        if (heatCount >= 16384) sizeKey = '16k';
        else if (heatCount >= 8192) sizeKey = '8k';
        else if (heatCount >= 4096) sizeKey = '4k';
        else if (heatCount >= 2048) sizeKey = '2k';
        else if (heatCount >= 1024) sizeKey = '1k';
        else sizeKey = String(heatCount); // Use heat count directly for smaller values
        
        psId = `racing-${gameKey}-${sizeKey}`;
      }
      
      const structure = PAYOUT_STRUCTURES[psId];
      
      // Get the structure name (e.g., "Paddock Sprint", "Choco Mountain Clash", "Showdown")
      const structureName = structureDisplayName(structure, critsEnabled) || (isShowdown ? 'Showdown' : `${heatCount} Heats`);
      
      // Calculate first place prize directly from structure bands
      // We need to do this manually since 'live' hasn't been calculated yet
      let firstPlacePrizeCents = 0;
      if (structure?.bands?.length > 0) {
        // Calculate net pool
        const grossPool = entriesExpected * critEntryCost; // 1 Crit = $1
        const rake = Math.floor(grossPool * 0.10);
        const netPool = Math.max(0, grossPool - rake);
        
        if (isShowdown) {
          // For Showdown, first place prize is in the first band
          const firstBand = structure.bands[0];
          if (firstBand && firstBand.percent) {
            firstPlacePrizeCents = Math.round(netPool * Number(firstBand.percent) / 100);
          }
        } else {
          // For Racing, find the Finals band (typically first band with subBands)
          const finalsBand = structure.bands.find(b => 
            b.stage === 'Finals' && Array.isArray(b.subBands)
          );
          
          if (finalsBand) {
            // Calculate Finals pool (band percent of net pool)
            const finalsPercent = Number(finalsBand.percent || 0);
            const finalsPool = Math.round(netPool * finalsPercent / 100);
            
            // Find Champion tier within Finals
            const championTier = finalsBand.subBands.find(sb => 
              sb.tier === 'Champion' || (sb.tier && sb.tier.includes('Champion'))
            );
            
            if (championTier) {
              // Calculate Champion prize (tier percent of Finals pool)
              const championPercent = Number(championTier.percentOfBand || 0);
              firstPlacePrizeCents = Math.round(finalsPool * championPercent / 100);
            }
          }
        }
      }
      
      const prizeText = firstPlacePrizeCents > 0 
        ? `${formatPrize(firstPlacePrizeCents)} to 1st`
        : '';
      
      // PUBLISHER COMPLIANCE: Event name must NOT include game name
      return {
        // Event name: NO game name (e.g., "Paddock Sprint $100 to 1st")
        eventName: prizeText ? `${structureName} ${prizeText}` : structureName,
        // Stream title: CAN include game abbrev for Twitch/YouTube categorization
        baseStreamTitle: `CritCash ${gameAbbr} ${structureName} ${prizeText}`.trim(),
      };
    }
    
    // Generic fallback for other modes
    // PUBLISHER COMPLIANCE: Event name must NOT include game name
    const firstPlacePrizeCents = calculateFirstPlacePrize();
    const prizeText = firstPlacePrizeCents > 0 
      ? `${formatPrize(firstPlacePrizeCents)} to 1st`
      : '';

    return {
      // Event name: NO game name
      eventName: prizeText ? `Event ${prizeText}` : 'Event',
      // Stream title: CAN include game abbrev
      baseStreamTitle: `CritCash ${gameAbbr} ${prizeText}`.trim(),
    };
  };

  const { eventName, baseStreamTitle } = getEventNaming();

  // AFTER - ADD isRacing and isShowdown parameters and heatCount to ev:
  const live = usePayouts({
    ev: {
      // Battle-royale showdowns must NOT hit usePayouts' entrant-scaled GT7
      // showdown bands (2-15 clamp): pass a mode that resolves no structure
      // and feed the static wz-* bands via the event-override path instead.
      mode: isBattleRoyale ? 'battle-royale' : (isShowdown ? 'showdown' : mode),  // Use 'showdown' as mode for payout resolution
      game,
      format: formatName,
      bracketSize: entriesExpected,
      maxTeams: entriesExpected,
      heatCount: heatCount,  // NEW: Add heat count for racing/showdown
      ...(isBattleRoyale
        ? { payoutBands: squadSize > 1 ? WZ_SQUADS_PREVIEW_BANDS : WZ_SOLOS_PREVIEW_BANDS }
        : {}),
    },
    entrants,
    // Feed the Crit cost into the SAME payout engine (1 Crit = $1). usePayouts
    // is currency-agnostic, so stageBands come out identical to the cash
    // structure, just in Crit units.
    entryFeeCents: critEntryCost,
    isTournament: isBracket,
    // Both racing and showdown use racing payout structures; BR uses the flat
    // wz bands above (buildStageBands path, not the racing subBand walker).
    isRacing: (isRacing || isShowdown) && !isBattleRoyale,
  });

  // IGN is required to create events
  const notEligible = !eligibilityLoading && !isEligible;
  // Track validation for racing games: require track selection
  const hasTrackIssue = gp?.type === 'racing' && gp?.tracks?.length > 0 && (
    ((isH2H || isShowdown) && selectedTracks.length < 1) ||
    (isRacing && selectedTracks.filter(Boolean).length < totalTrackRounds)
  );
  // Ladder min-entry (server-enforced; this is the friendly client mirror):
  // paid sizes force a floor on the per-entrant Crit entry cost.
  const selectedLadderSize = isRacing ? heatCount : isBracket ? bracketSize : 0;
  const ladderMinEntry = (critsEnabled && !isAdmin && selectedLadderSize) ? sizeTierUI(selectedLadderSize).minEntry : 0;
  const entryBelowMin = ladderMinEntry > 0 && critEntryCost < ladderMinEntry;
  // Crits OFF: the size ladder is gated by membership instead of an unlock
  // fee — sizes 2-16 are open to everyone, above 16 is for members (admins
  // exempt). Mirrors the server's size-requires-membership rule.
  const sizeNeedsMembership = !critsEnabled && !isAdmin && selectedLadderSize > 16 && !isPaidMember;

  // The creator's TRUE charge — mirrors createEventWithHold exactly:
  //   max(entry, size unlock) + custom-title fee (Rookie 5; Pro/Elite free).
  // Shown itemized in the confirm modal so "Spend N Crits" never disagrees
  // with what the server debits (founder 2026-07-13: the 30-shown/250-charged
  // mismatch fed straight into the you're-short modal with no explanation).
  const creatorUnlockFee = (critsEnabled && !isAdmin && selectedLadderSize) ? sizeTierUI(selectedLadderSize).unlockFee : 0;
  const creatorTitleFee = (critsEnabled && !isAdmin && useCustomTitle && customEventTitle.trim())
    ? (['pro', 'elite'].includes(userTier) ? 0 : 5)
    : 0;
  const creatorTotalCrits = (isAdmin || !critsEnabled) ? 0
    : Math.max(critEntryCost, creatorUnlockFee) + creatorTitleFee;

  // Host round selection only applies to bracket host mode (one match per
  // round) - showdown/BR host events are a single lobby with no round picks.
  const createDisabled = !game || !formatName || (isBracket && !bracketSize) || (isRacing && !heatCount) || notEligible || hasTrackIssue || entryBelowMin || sizeNeedsMembership || (isHostMode && isBracket && hostSelectedRounds.length === 0)
    // "Other" community game needs a sluggable name before anything else.
    || (game === 'other' && !effectiveGameKey);


  // Validate and show confirmation modal
  const onCreate = async () => {
    if (createDisabled) return;
    
    if (!isEligible && !eligibilityLoading) {
      if (!hasStreamKey) {
        Alert.alert(
          'Stream Key Required',
          'You need to set up your CritCash stream key before creating an event.\n\nAll participants must stream their gameplay for verification.',
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'Set Up Streaming', 
              onPress: () => navigation.navigate('StreamingAccounts')
            }
          ]
        );
        return;
      }
      if (!hasIGN) {
        Alert.alert(
          hasAnyIGN ? `${platformStr} Profile Required` : 'IGN Required',
          hasAnyIGN
            ? `This will be a ${platformStr}-only event. Add ${platformStr} with your in-game name to your ${gp?.displayName || game} profile before creating it.`
            : `You need to set your in-game name for ${gp?.displayName || game} before creating an event.`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Set IGN Now',
              onPress: () => navigation.navigate('GameProfiles', { highlightGame: effectiveGameKey || game, communityGameName: game === 'other' ? communityName : undefined, communityGameGenre: game === 'other' ? otherGenre : undefined })
            }
          ]
        );
        return;
      }
    }
    
    // Hosts and admins skip payment checks
    if (!isHostMode && !isAdmin) {
      // Check if user has enough balance
      if (totalBalanceCents < entryFeeCents) {
        Alert.alert(
          'Insufficient Balance',
          `You need ${fmtUSD(entryFeeCents - totalBalanceCents)} more to create this event.\n\nEntry fee: ${fmtUSD(entryFeeCents)}\nYour balance: ${fmtUSD(totalBalanceCents)}`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Add Funds',
              onPress: () => navigation.navigate('Wallet')
            }
          ]
        );
        return;
      }
    }

    // All validations passed - show confirmation modal
    setShowCreateConfirmModal(true);
  };

  // Actually create the event after user confirms
  const confirmCreate = async () => {
    setShowCreateConfirmModal(false);
    setIsCreating(true);
    
    const payoutStructures = Object.values(PAYOUT_STRUCTURES);

    try {
      await handleCreateEvent({
        user,
        isAdmin,
        showError,
        // Members-only rejection -> in-place upgrade funnel (plan pick +
        // embedded Stripe checkout inside a modal, form stays behind the
        // scrim). No navigation — leaving the screen = abandoned cart.
        onMembershipRequired: () => setShowUpgradeModal(true),
        // Balance short -> in-place Crit-pack funnel with the exact shortfall.
        onInsufficientCrits: (shortfall) => setCritShortfall(shortfall || {}),
        setBusy: () => {},
        setSuccessData: (data) => {
          if (data?.eventId && data?.collection) {
            navigation.navigate('Lobby', { 
              highlightEvent: data.eventId,
              collection: data.collection 
            });
          }
        },
        setRosterModalOpen: () => {},
        payoutStructures,

        mode: isShowdown ? 'showdown' : mode,  // Use 'showdown' as mode if showdown selected
        creatorRole: isHostMode ? 'host' : 'racer',
        hostSelectedRounds: isHostMode ? hostSelectedRounds : [],
        game,
        // Open-catalog: the backend slugs 'other' + gameName to a shared
        // cg_* key; browse-catalog keys pass through with their own profile.
        // Genre matches the game to its category + event modes (canonical
        // for catalog extras — server ignores it there).
        gameName: game === 'other' ? communityName : undefined,
        gameGenre: game === 'other' ? otherGenre : undefined,
        category,
        formatName,
        teamSize,
        entryUnit,
        entryFeeCents,
        critEntryCost,
        platform: platformStr,
        crossplay,
        visibility,
        bracketSize: isBracket ? entriesExpected : undefined,
        gridSize: (isShowdown || isRacing) ? effectiveGridSize : undefined,
        hostRaces: hostRacesActive || undefined,
        heatCount: isRacing ? heatCount : undefined,  // Only pass heatCount for racing, not showdown
        // Warzone battle royale: discriminator flag + in-game squad size +
        // the game's start floor (under it at lock = auto-cancel + refund).
        battleRoyale: isBattleRoyale ? true : undefined,
        squadSize: isBattleRoyale ? squadSize : undefined,
        minEntrants: isBattleRoyale ? (Number(selectedFormatData?.minPlayers) || undefined) : undefined,
        entriesExpected,
        startMode,
        startInMins,
        fillDelayMins,
        // Inter-round timing — backend stores these only for multi-round events
        // (validateRoundSchedule). Gap mode: next round fires this long after the
        // previous round completes.
        roundScheduleMode: 'gap',
        interRoundGapMinutes,
        // Paid custom title (server moderates + charges; admins free)
        customTitle: useCustomTitle && customEventTitle.trim() ? customEventTitle.trim() : undefined,
        creatorRoomId: (isH2H && game === 'gt7') ? (creatorRoomId || null) : null,
        dayOffset: startMode === 'absolute' ? Math.floor((absDate - new Date()) / (24 * 60 * 60 * 1000)) : 0,
        absHour: startMode === 'absolute' ? absDate.getHours() : 0,
        absMinute: startMode === 'absolute' ? absDate.getMinutes() : 0,
        eventName,
        requiredStreamTitle: baseStreamTitle,

        // Madden in-game settings (skill, quarter length, clock, style, etc.)
        ...(game === 'mdn' ? { gameSettings: maddenSettings } : {}),

        // GT7-specific fields (racing games with track/category data)
        ...(gp?.type === 'racing' ? {
          selectedTracks: selectedTracks.filter(Boolean),
          carCategory: selectedCarCategory,
          // Flatten the rich GT7_LOBBY_SETTINGS schema down to bare values,
          // applying any host overrides from the Advanced Settings panel.
          // Result matches the legacy flat shape so EventDetailScreen and
          // existing event docs continue to work without migration.
          lobbySettings: game === 'gt7'
            ? (() => {
                const base = extractLobbyValues(GT7_LOBBY_SETTINGS, lobbySettingsOverrides);
                // Inject host's race-shape overrides into raceSettings so
                // EventDetail can render the actual laps/pits/tire the host
                // configured rather than the format preset's defaults.
                return {
                  ...base,
                  raceSettings: {
                    ...(base.raceSettings || {}),
                    ...(isEndurance
                      ? { raceTimeMinutes: enduranceMinutes }
                      : { raceLaps: lapsOverride }),
                    minPitStops: pitStopsOverride,
                    requiredTireTypeChange: tireChangeOverride,
                  },
                };
              })()
            : undefined,
        } : {}),
      });
    } catch (error) {
      console.error('Event creation failed:', error);
      
      // Parse the error message for user-friendly display
      const errorMessage = error?.message || 'Unknown error occurred';
      
      // Check for specific error types and show appropriate alerts
      if (errorMessage.includes('stream-key-required')) {
        Alert.alert(
          'Stream Key Required',
          'You must set up your CritCash stream key before creating an event.\n\nGo to Profile Streaming to set it up.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Set Up Streaming', onPress: () => navigation.navigate('StreamingAccounts') }
          ]
        );
      } else if (errorMessage.includes('ign-required') || errorMessage.includes('ign-invalid')) {
        Alert.alert(
          'Game Profile Required',
          `You must set your in-game name (IGN) for ${gp?.displayName || game} before creating an event.`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Set Up IGN', onPress: () => navigation.navigate('GameProfiles', { highlightGame: effectiveGameKey || game, communityGameName: game === 'other' ? communityName : undefined, communityGameGenre: game === 'other' ? otherGenre : undefined }) }
          ]
        );
      } else if (errorMessage.includes('insufficient') || errorMessage.includes('balance')) {
        Alert.alert(
          'Not Enough Crits',
          'You don\'t have enough Crits to create this event. Get more Crits to continue.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Get Crits', onPress: () => navigation.navigate('Subscription') }
          ]
        );
      } else {
        // Generic error
        Alert.alert(
          'Event Creation Failed',
          errorMessage.split(':').pop().trim() || 'Something went wrong. Please try again.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Get available modes for the current game. Each multi-round format
  // (bracket tournaments, racing heats) disappears for non-admins while its
  // admin hide toggle is on.
  const availableModes = useMemo(() => {
    const allowed = gp?.allowedModes || [];
    const tournamentsHidden = hiddenModes.tournaments && !isAdmin;
    const heatsHidden = hiddenModes.heats && !isAdmin;
    // Shooters with battle-royale formats (Warzone) reuse the showdown chassis
    // - surfaced as 'Battle Royale' and gated on allowedModes + a BR format.
    const hasBRFormat = Object.values(gp?.formats || {}).some((f) => f?.battleRoyale && !f?.legacy);
    // Open-catalog racing games (Mario Kart World, F1 25) run Showdown too —
    // their profile grants the mode only when a payout-structure family
    // exists for the grid (communityGames buildCommunityGameProfile).
    const isCommunityRacing = gp.isCommunity === true && gp.genre === 'racing';
    return [
      { key: 'h2h', label: 'Head-to-Head', enabled: allowed.includes('h2h') },
      { key: 'bracket', label: 'Tournament', enabled: allowed.includes('bracket') && !tournamentsHidden },
      { key: 'racing', label: 'Heats', enabled: allowed.includes('racing') && gp.type === 'racing' && !heatsHidden },
      // Showdown is available for all racing games (payout structures exist for all)
      {
        key: 'showdown',
        label: (gp.type === 'racing' || isCommunityRacing) ? 'Showdown' : 'Battle Royale',
        enabled: gp.type === 'racing'
          || (allowed.includes('showdown') && (hasBRFormat || gp.isCommunity === true)),
      },
    ].filter(m => m.enabled);
  }, [gp, hiddenModes, isAdmin]);

  // If the mode the user had selected just got hidden (or a game switch left
  // an unavailable mode selected), snap to the first mode that's still there.
  useEffect(() => {
    if (availableModes.length && !availableModes.some((m) => m.key === selectedMode)) {
      setSelectedMode(availableModes[0].key);
      setSelectedTracks([]);
    }
  }, [availableModes, selectedMode]);

  // Check if a category has any available games
  const categoryHasGames = useCallback((cat) => {
    if (cat === 'community') return true; // always has "Other"
    if (extrasForCat(cat).length > 0) return true; // open-catalog games count
    return availableGameKeys.some((k) => (GAME_PROFILES[k]?.type || '') === cat);
  }, [availableGameKeys, extrasForCat]);

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        <Text style={styles.h1}>Create New Event</Text>
        <Text style={styles.sub}>Set up your competitive event</Text>

        {/* Category */}
        <View style={styles.panel}>
          <Text style={styles.h2}>Category</Text>
          <View style={styles.pillRow}>
            {categories
              .filter((cat) => categoryHasGames(cat))
              .map((cat) => (
                <Pill
                  key={cat}
                  label={cat === 'community' ? 'More Games'
                    : cat === 'rts' ? 'Strategy'
                      : cat === 'moba' ? 'MOBA'
                        : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  active={category === cat}
                  onPress={() => {
                    setCategory(cat);
                    const g = firstAvailableGameForCat(cat);
                    if (g) {
                      setGame(g);
                      resetForGame(g, cat);
                    }
                  }}
                />
              ))}
          </View>
        </View>

        {/* Mode Selection */}
        {availableModes.length > 1 && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Event Mode</Text>
            <View style={styles.pillRow}>
              {availableModes.map((modeOption) => (
                <Pill
                  key={modeOption.key}
                  label={modeOption.label}
                  active={selectedMode === modeOption.key}
                  onPress={() => {
                    setSelectedMode(modeOption.key);
                    setSelectedTracks([]); // Reset tracks when mode changes
                    // Reset size options when changing modes
                    if (modeOption.key === 'racing') {
                      setHeatCount(isAdmin ? 16 : 8);
                      setBracketSize(0);
                    } else if (modeOption.key === 'showdown') {
                      // Showdown doesn't need heat count - grid size comes from payout structure
                      setBracketSize(0);
                      setHeatCount(0);
                    } else if (modeOption.key === 'bracket') {
                      setBracketSize(isAdmin ? 16 : 8);
                      setHeatCount(0);
                    }
                  }}
                />
              ))}
            </View>
            {isH2H && (
              <Text style={styles.note}>
                Head-to-head - one match, two sides, any team size the format allows.{critsEnabled ? ' Winner takes the whole Crit pool.' : ''}
              </Text>
            )}
            {isBracket && (
              <Text style={styles.note}>
                Single-elimination tournament bracket with multiple rounds.
              </Text>
            )}
            {isRacing && (
              <Text style={styles.note}>
                Multiple heats with fastest times advancing to finals.
              </Text>
            )}
            {isShowdown && gp.type === 'racing' && (
              <Text style={styles.note}>
                Single decisive race{critsEnabled ? ' where the top half of finishers earn prizes' : ''}. Every position counts.
              </Text>
            )}
            {isShowdown && gp.isCommunity && gp.genre === 'racing' && (
              <Text style={styles.note}>
                Single decisive race{critsEnabled ? ' where the top half of finishers earn prizes' : ''}. You set up the
                race lobby in-game; everyone races on stream and reports their finish at the end.
              </Text>
            )}
            {isShowdown && gp.type !== 'racing' && !gp.isCommunity && (
              <View style={{ marginTop: 10, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                  Battle Royale
                </Text>
                <Text style={[styles.note, { marginTop: 2 }]}>
                  One private-match lobby for the whole event - final placement decides the {critsEnabled ? 'payouts' : 'standings'}.
                </Text>
                <Text style={[styles.note, { marginTop: 4 }]}>
                  A host is required. Create as Host only (spectate via the CODCaster slot,
                  no player slot taken) or Host &amp; play (you drop in too, taking a normal
                  player slot). If no host has claimed the event by lock time, the event is
                  cancelled{critsEnabled ? ' and every entry is refunded' : ''}.
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Game */}
        <View style={styles.panel}>
          <Text style={styles.h2}>Game</Text>
          
          {/* Loading indicator while fetching availability */}
          {availabilityLoading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color="#FCD000" />
              <Text style={styles.loadingText}>Loading games...</Text>
            </View>
          ) : (
            <>
              <View style={styles.pillRow}>
                {/* Only show enabled games - disabled games are hidden. With
                    Battle Royale / Showdown selected, only games that can run
                    a single-lobby event stay listed (racing profiles, or a
                    catalog entry with at least one battleRoyale format).
                    "More Games" lists the open catalog: browse-catalog games
                    plus a free-text "Other" — any game is playable. */}
                {(category === 'community'
                  ? [...extrasForCat('other'), 'other']
                  : [
                    ...availableGameKeys.filter((k) => (GAME_PROFILES[k]?.type || '') === category),
                    // Open-catalog games ride with their genre (Mario Kart
                    // World under Racing, SC2 under Strategy, ...).
                    ...extrasForCat(category),
                  ])
                  .filter((k) => {
                    if (!isShowdown || k === 'other') return true;
                    const p = GAME_PROFILES[k];
                    if (p) {
                      return p.type === 'racing'
                        || Object.values(p.formats || {}).some((f) => f?.battleRoyale === true);
                    }
                    return buildCommunityGameProfile(k).allowedModes.includes('showdown');
                  })
                  .map((k) => (
                    <Pill
                      key={k}
                      label={k === 'other'
                        ? 'Other…'
                        : (GAME_PROFILES[k]?.displayName || EXTRA_EVENT_GAMES[k]?.displayName || k)}
                      active={game === k}
                      onPress={() => {
                        setGame(k);
                        resetForGame(k, category);
                      }}
                    />
                  ))}
              </View>

              {/* Free-text game name for "Other" — normalized server-side so
                  the same game from two creators lands on one shared key. */}
              {category === 'community' && game === 'other' && (
                <>
                  <TextInput
                    style={styles.customGameInput}
                    value={customGameName}
                    onChangeText={setCustomGameName}
                    placeholder="What game is it?"
                    placeholderTextColor="#666"
                    maxLength={50}
                  />
                  {/* Known games (community catalog + browse extras) carry a
                      canonical name and genre — the picker only shows for
                      games the catalog doesn't know. */}
                  {isCatalogMatch ? (
                    <Text style={styles.note}>
                      Matched: {gp?.displayName} — {(COMMUNITY_GENRES.find((g) => g.key === gp?.genre)?.label) || 'Other'}.
                      Name and category are set automatically.
                    </Text>
                  ) : (
                    <>
                      {/* Genre matches the game to its category and event modes. */}
                      <Text style={styles.customGenreLabel}>Genre</Text>
                      <View style={styles.pillRow}>
                        {COMMUNITY_GENRES.map((g) => (
                          <Pill
                            key={g.key}
                            label={g.label}
                            active={otherGenre === g.key}
                            onPress={() => setOtherGenre(g.key)}
                          />
                        ))}
                      </View>
                    </>
                  )}
                </>
              )}
              {/* Player-reported results apply to EVERY non-vision game —
                  full-profile games (Tekken, NBA 2K, ...) included, not just
                  the More Games category. Heats are admin-resolved, so mode
                  'racing' skips the notice. */}
              {usesManualResults && !isRacing && (
                <Text style={styles.note}>
                  {category === 'community' ? 'Every game is welcome here. ' : ''}
                  Results are reported by the players: when the {isShowdown ? 'race' : 'match'} ends,
                  everyone taps MATCH ENDED and reports their {isShowdown ? 'finish' : 'result'} —
                  matching reports post instantly, and anything contested gets reviewed.
                </Text>
              )}

              {/* Show message if no games available in category */}
              {category !== 'community'
                && extrasForCat(category).length === 0
                && availableGameKeys.filter((k) => (GAME_PROFILES[k]?.type || '') === category).length === 0 && (
                <View style={styles.noGamesMessage}>
                  <Text style={styles.noGamesText}>
                    No games currently available in this category
                  </Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* Platform Selection */}
        {gp?.allowedPlatforms && gp.allowedPlatforms.length > 0 && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Platform</Text>
            <View style={styles.pillRow}>
              {gp.allowedPlatforms.map((plat) => (
                <Pill
                  key={plat}
                  label={plat}
                  active={selectedPlatform === plat}
                  onPress={() => setSelectedPlatform(plat)}
                />
              ))}
            </View>
            {crossplay ? (
              <Text style={styles.note}>
                Cross-platform play enabled - players from all platforms can compete together.
              </Text>
            ) : selectedPlatform ? (
              <Text style={styles.note}>
                {selectedPlatform} players only — entry requires a {selectedPlatform} in-game name on the player's game profile. Great for keeping the field on even footing.
              </Text>
            ) : null}
          </View>
        )}

        {/* Stream Key Warning Banner - Show First (more important) */}
        {!eligibilityLoading && !hasStreamKey && (
          <TouchableOpacity 
            style={styles.streamKeyWarningBanner}
            onPress={() => navigation.navigate('StreamingAccounts')}
          >
            <View style={styles.warningContent}>
              <Text style={styles.warningIcon}></Text>
              <View style={styles.warningText}>
                <Text style={styles.warningTitle}>Stream Key Required</Text>
                <Text style={styles.warningSubtitle}>
                  Set up your CritCash stream key to create events
                </Text>
              </View>
              <Text style={styles.warningArrow}></Text>
            </View>
          </TouchableOpacity>
        )}

        {/* IGN Warning Banner - Show if stream key is set but IGN is missing */}
        {!eligibilityLoading && hasStreamKey && !hasIGN && game && (
          <TouchableOpacity 
            style={styles.ignWarningBanner}
            onPress={() => navigation.navigate('GameProfiles', { highlightGame: effectiveGameKey || game, communityGameName: game === 'other' ? communityName : undefined, communityGameGenre: game === 'other' ? otherGenre : undefined })}
          >
            <View style={styles.warningContent}>
              <Text style={styles.warningIcon}></Text>
              <View style={styles.warningText}>
                <Text style={styles.warningTitle}>
                  {hasAnyIGN ? `${platformStr} Profile Required` : 'IGN Required'}
                </Text>
                <Text style={styles.warningSubtitle}>
                  {hasAnyIGN
                    ? `This will be a ${platformStr}-only event — add ${platformStr} with your in-game name to your ${gp?.displayName || game} profile`
                    : `Set your in-game name for ${gp?.displayName || game} to create events`}
                </Text>
              </View>
              <Text style={styles.warningArrow}></Text>
            </View>
          </TouchableOpacity>
        )}

        {/* All Set Confirmation */}
        {!eligibilityLoading && isEligible && game && (
          <View style={styles.eligibleBanner}>
            <Text style={styles.eligibleIcon}></Text>
            <Text style={styles.eligibleText}>
              Ready to create Playing as: <Text style={styles.eligibleValue}>{ign}</Text>{serviceAccountName ? <Text style={{ color: '#9aa0b4' }}>{' | '}{serviceShortName}: <Text style={styles.eligibleValue}>{serviceAccountName}</Text></Text> : null}
            </Text>
          </View>
        )}

        {/* IGN Set Confirmation */}
        {!eligibilityLoading && hasIGN && game && (
          <View style={styles.ignSetBanner}>
            <Text style={styles.ignSetIcon}></Text>
            <Text style={styles.ignSetText}>
              Playing as: <Text style={styles.ignSetValue}>{ign}</Text>{serviceAccountName ? <Text style={{ color: '#9aa0b4' }}>{' | '}{serviceShortName}: <Text style={styles.ignSetValue}>{serviceAccountName}</Text></Text> : null}
            </Text>
            <TouchableOpacity onPress={() => navigation.navigate('GameProfiles', { highlightGame: effectiveGameKey || game, communityGameName: game === 'other' ? communityName : undefined, communityGameGenre: game === 'other' ? otherGenre : undefined })}>
              <Text style={styles.ignEditLink}>Edit</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Visibility */}
        <View style={styles.panel}>
          <Text style={styles.h2}>Visibility</Text>
          <View style={styles.pillRow}>
            <Pill
              label="Public"
              active={visibility === 'public'}
              onPress={() => setVisibility('public')}
            />
            <Pill
              label="Private"
              active={visibility === 'private'}
              onPress={() => setVisibility('private')}
            />
          </View>
          <Text style={styles.note}>
            {visibility === 'public' 
              ? 'Event will be visible in public listings and anyone can join.'
              : 'Event will be hidden from listings. Only players with the direct link can join.'}
          </Text>
        </View>

        {/* Race Configuration (GT7) / Format (other games) — for GT7 the
            format pills are hidden; the slider/stepper/toggle drive the
            actual race shape, swapping to a duration slider in Endurance mode. */}
        <View style={styles.panel}>
          <Text style={styles.h2}>
            {game === 'gt7' ? 'Race Configuration' : game === 'mdn' ? 'Game Settings' : 'Format'}
          </Text>
          {game !== 'gt7' && game !== 'mdn' && (
            <>
              <View style={styles.pillRow}>
                {formatOptions.map((fmt) => (
                  <Pill
                    key={fmt}
                    label={fmt}
                    active={formatName === fmt}
                    onPress={() => {
                      setFormatName(fmt);
                      // BR playlists only run as showdown (Battle Royale)
                      // events - snap the mode. Normally unreachable since
                      // formatOptions filters BR out of bracket/h2h.
                      if (gp?.formats?.[fmt]?.battleRoyale && selectedMode !== 'showdown') {
                        setSelectedMode('showdown');
                      }
                    }}
                  />
                ))}
              </View>
              {gp?.formats?.[formatName]?.description && (
                <Text style={styles.note}>{gp.formats[formatName].description}</Text>
              )}
            </>
          )}

          {/* Madden Game Settings — full in-game options replace the old
              two-preset format picker. formatName stays 'Standard'; the real
              match shape is stored on ev.gameSettings. */}
          {game === 'mdn' && (
            <View>
              {Object.entries(MADDEN_GAME_SETTINGS).map(([key, def]) => (
                <View key={key} style={{ marginTop: 12 }}>
                  {def.type === 'minutes' ? (
                    <>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={styles.note}>{def.label}</Text>
                        <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                          {maddenSettings[key]} Min
                        </Text>
                      </View>
                      {Platform.OS === 'web' ? (
                        <input
                          type="range"
                          min={String(def.min)}
                          max={String(def.max)}
                          value={maddenSettings[key]}
                          onChange={(e) => setMaddenSetting(key, Number(e.target.value))}
                          style={{ width: '100%', accentColor: '#ffd166', marginTop: 4 }}
                        />
                      ) : (
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 4 }}>
                          <TouchableOpacity onPress={() => setMaddenSetting(key, Math.max(def.min, maddenSettings[key] - 1))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                            <Text style={{ color: '#ffd166', fontSize: 18 }}>-</Text>
                          </TouchableOpacity>
                          <Text style={{ color: '#fff', minWidth: 70, textAlign: 'center', fontSize: 16 }}>{maddenSettings[key]} Min</Text>
                          <TouchableOpacity onPress={() => setMaddenSetting(key, Math.min(def.max, maddenSettings[key] + 1))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                            <Text style={{ color: '#ffd166', fontSize: 18 }}>+</Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </>
                  ) : (
                    <>
                      <Text style={styles.note}>{def.label}</Text>
                      <View style={[styles.pillRow, { marginTop: 4 }]}>
                        {def.options.map((opt) => (
                          <Pill
                            key={opt}
                            label={opt}
                            active={maddenSettings[key] === opt}
                            onPress={() => setMaddenSetting(key, opt)}
                          />
                        ))}
                      </View>
                    </>
                  )}
                </View>
              ))}
              <View style={{ marginTop: 12, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                  Estimated Game Time: ~{estimateMaddenDurationMinutes(maddenSettings.quarterLength)} min
                </Text>
                <Text style={[styles.note, { fontSize: 10, color: '#888', marginTop: 2 }]}>
                  Based on {maddenSettings.quarterLength}-minute quarters. You can edit these settings from the event page until the event starts.
                </Text>
              </View>
            </View>
          )}
          {gp?.formats?.[formatName]?.laps !== undefined && (
            <View style={{ marginTop: 12 }}>
              {isEndurance ? (
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.note}>Race Duration</Text>
                    <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>{formatEnduranceTime(enduranceMinutes)}</Text>
                  </View>
                  {Platform.OS === 'web' ? (
                    <input
                      type="range"
                      min="1"
                      max="1440"
                      value={enduranceMinutes}
                      onChange={(e) => setEnduranceMinutes(Number(e.target.value))}
                      style={{ width: '100%', accentColor: '#ffd166', marginTop: 4 }}
                    />
                  ) : (
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 4 }}>
                      <TouchableOpacity onPress={() => setEnduranceMinutes(Math.max(1, enduranceMinutes - 5))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                        <Text style={{ color: '#ffd166', fontSize: 18 }}>-</Text>
                      </TouchableOpacity>
                      <Text style={{ color: '#fff', minWidth: 90, textAlign: 'center', fontSize: 16 }}>{formatEnduranceTime(enduranceMinutes)}</Text>
                      <TouchableOpacity onPress={() => setEnduranceMinutes(Math.min(1440, enduranceMinutes + 5))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                        <Text style={{ color: '#ffd166', fontSize: 18 }}>+</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  <Text style={[styles.note, { fontSize: 10, color: '#888', marginTop: 4 }]}>
                    Endurance races end after the configured duration. GT7 supports up to 24h.
                  </Text>
                </>
              ) : (
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.note}>Laps</Text>
                    <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>{lapsOverride}</Text>
                  </View>
                  {Platform.OS === 'web' ? (
                    <input
                      type="range"
                      min="1"
                      max="200"
                      value={lapsOverride}
                      onChange={(e) => setLapsOverride(Number(e.target.value))}
                      style={{ width: '100%', accentColor: '#ffd166', marginTop: 4 }}
                    />
                  ) : (
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 4 }}>
                      <TouchableOpacity onPress={() => setLapsOverride(Math.max(1, lapsOverride - 1))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                        <Text style={{ color: '#ffd166', fontSize: 18 }}>-</Text>
                      </TouchableOpacity>
                      <Text style={{ color: '#fff', minWidth: 50, textAlign: 'center', fontSize: 16 }}>{lapsOverride}</Text>
                      <TouchableOpacity onPress={() => setLapsOverride(Math.min(200, lapsOverride + 1))} style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#1e2025', borderRadius: 6 }}>
                        <Text style={{ color: '#ffd166', fontSize: 18 }}>+</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              )}

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
                <Text style={[styles.note, { flex: 1 }]}>Mandatory Pit Stops</Text>
                <TouchableOpacity onPress={() => setPitStopsOverride(Math.max(0, pitStopsOverride - 1))} style={{ paddingHorizontal: 12, paddingVertical: 4, backgroundColor: '#1e2025', borderRadius: 6 }}>
                  <Text style={{ color: '#ffd166', fontSize: 16 }}>-</Text>
                </TouchableOpacity>
                <Text style={{ color: '#fff', minWidth: 36, textAlign: 'center', fontSize: 14 }}>{pitStopsOverride}</Text>
                <TouchableOpacity onPress={() => setPitStopsOverride(Math.min(5, pitStopsOverride + 1))} style={{ paddingHorizontal: 12, paddingVertical: 4, backgroundColor: '#1e2025', borderRadius: 6 }}>
                  <Text style={{ color: '#ffd166', fontSize: 16 }}>+</Text>
                </TouchableOpacity>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
                <Text style={[styles.note, { flex: 1 }]}>Required Tire Type Change</Text>
                <Pill
                  label={tireChangeOverride ? 'Required' : 'Off'}
                  active={tireChangeOverride}
                  onPress={() => setTireChangeOverride(!tireChangeOverride)}
                />
              </View>

              {/* Duration estimate — only for lap races. In endurance the
                  configured time IS the duration, so no estimate needed. */}
              {!isEndurance && (() => {
                const firstTrackName = selectedTracks[0];
                const firstTrack = firstTrackName && gp.tracks?.find(t => t.name === firstTrackName);
                if (!firstTrack) {
                  return (
                    <Text style={[styles.note, { marginTop: 12, fontStyle: 'italic', color: '#888' }]}>
                      Pick a track below to see estimated race duration.
                    </Text>
                  );
                }
                const minutes = estimateRaceMinutes(firstTrack, lapsOverride, selectedCarCategory, pitStopsOverride);
                const display = minutes >= 60
                  ? `~${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`
                  : `~${Math.round(minutes)} min`;
                return (
                  <View style={{ marginTop: 12, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                    <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                      Estimated Race Duration: {display}
                    </Text>
                    <Text style={[styles.note, { fontSize: 10, color: '#888', marginTop: 2 }]}>
                      Based on {firstTrack.name} ({firstTrack.lengthMiles} mi), {lapsOverride} laps, {(gp.roomSettings?.common?.carCategory?.options || []).find(o => o.key === selectedCarCategory)?.label || selectedCarCategory}. Rough estimate.
                    </Text>
                  </View>
                );
              })()}
            </View>
          )}
        </View>

        {/* Squad Size - Warzone BR playlists. Squads are in-game groupings:
            every player enters (and pays) individually, then squads form in
            the game lobby. Event teamSize stays 1 - no team machinery. */}
        {isBattleRoyale && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Squad Size</Text>
            <View style={styles.pillRow}>
              {(selectedFormatData?.teamSizes || [1]).map((size) => (
                <Pill
                  key={size}
                  label={SQUAD_SIZE_LABELS[size] || `${size}`}
                  active={squadSize === size}
                  onPress={() => setSquadSize(size)}
                />
              ))}
            </View>
            <Text style={styles.note}>
              {squadSize === 1
                ? `Everyone drops in solo - individual placement decides the ${critsEnabled ? 'payouts' : 'standings'}.`
                : `Players enter individually and squad up to ${squadSize} in the game lobby. Squadmates share a placement${critsEnabled ? " and split that placement's prize evenly" : ''}.`}
            </Text>
          </View>
        )}

        {/* Lobby capacity + min-to-start - Warzone BR. gridSize is the
            EFFECTIVE cap: the format's (squad-size-tuned) private-lobby cap
            clamped to BR_PLATFORM_MAX_PLAYERS while scoring is manual; when
            clamped, the copy shows both truths. A Host-only CODCaster never
            takes a player slot; a Host & play creator takes a normal slot
            inside the cap. minPlayers is the GAME's floor to start a
            private match: under it at lock the event auto-cancels and
            refunds every entry. */}
        {isBattleRoyale && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Lobby</Text>
            <Text style={styles.note}>
              {brCapClamped
                ? `${gridSize} players (${brSizeCap} supported in-game - platform cap ${BR_PLATFORM_MAX_PLAYERS} while scoring is manual)`
                : `${gridSize} players`} - 1 lobby - {SQUAD_SIZE_LABELS[squadSize] || 'Solos'}
              {'  '}{hostRacesActive
                ? '(you host AND play — your seat counts in the cap)'
                : '(+ the CODCaster host, who does not take a player slot)'}
            </Text>
            <View style={{ marginTop: 10, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
              <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                Minimum to start: {brMinPlayers} players
              </Text>
              <Text style={[styles.note, { marginTop: 2 }]}>
                Warzone private matches will not start below {brMinPlayers} players - the
                event auto-cancels{critsEnabled ? ' and refunds every entry' : ''} if it doesn't reach {brMinPlayers}.
              </Text>
            </View>
          </View>
        )}

        {/* Vehicle Category — GT7 racing games with carCategory defined */}
        {gp?.roomSettings?.common?.carCategory && (isRacing || isShowdown || isH2H) && (
          <View style={styles.panel}>
            <Text style={styles.h2}>{gp.roomSettings.common.carCategory.label || 'Vehicle Category'}</Text>
            <View style={styles.pillRow}>
              {gp.roomSettings.common.carCategory.options.map((opt) => (
                <Pill
                  key={opt.key}
                  label={opt.label}
                  active={selectedCarCategory === opt.key}
                  onPress={() => setSelectedCarCategory(opt.key)}
                />
              ))}
            </View>
            {gp.roomSettings.common.carCategory.description && (
              <Text style={styles.note}>{gp.roomSettings.common.carCategory.description}</Text>
            )}
          </View>
        )}

        {/* Track Selection — Racing games with tracks defined */}
        {gp?.type === 'racing' && Array.isArray(gp?.tracks) && gp.tracks.length > 0 && (isH2H || isShowdown || isRacing) && (
          <View style={styles.panel}>
            <Text style={styles.h2}>
              {(isH2H || isShowdown) ? 'Track' : 'Track Selection (1 per Round)'}
            </Text>
            {isRacing && (
              <Text style={styles.note}>
                All heats in a round share the same track. Select a track for each round.
              </Text>
            )}
            {Array.from({ length: (isH2H || isShowdown) ? 1 : totalTrackRounds }, (_, i) => {
              const roundLabel = (isH2H || isShowdown)
                ? null
                : i === totalTrackRounds - 1
                  ? `Finals`
                  : `Round ${i + 1}`;
              return (
                <View key={i} style={{ marginTop: i > 0 ? 8 : 4 }}>
                  {roundLabel && (
                    <Text style={[styles.label, { marginBottom: 4 }]}>{roundLabel}</Text>
                  )}
                  <TouchableOpacity
                    style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                    onPress={() => {
                      // Toggle dropdown for this index
                      setTrackDropdownOpen(prev => prev === i ? -1 : i);
                      setTrackSearch('');
                    }}
                  >
                    <Text style={{ color: selectedTracks[i] ? '#fff' : '#666', flex: 1 }} numberOfLines={1}>
                      {selectedTracks[i] || 'Select a track...'}
                    </Text>
                    <Text style={{ color: '#888', fontSize: 12 }}>
                      {selectedTracks[i] && gp.tracks.find(t => t.name === selectedTracks[i])
                        ? `${gp.tracks.find(t => t.name === selectedTracks[i]).lengthMiles} mi`
                        : ''}
                    </Text>
                    <Ionicons name={trackDropdownOpen === i ? 'chevron-up' : 'chevron-down'} size={16} color="#888" style={{ marginLeft: 8 }} />
                  </TouchableOpacity>
                  {trackDropdownOpen === i && (
                    <View style={{ backgroundColor: '#1b1e24', borderRadius: 8, borderWidth: 1, borderColor: '#2a2f36', maxHeight: 280, marginTop: 4 }}>
                      <TextInput
                        style={[styles.input, { margin: 8, marginBottom: 4 }]}
                        placeholder="Search tracks..."
                        placeholderTextColor="#666"
                        value={trackSearch}
                        onChangeText={setTrackSearch}
                      />
                      <ScrollView style={{ maxHeight: 280 }} nestedScrollEnabled>
                        {(() => {
                          const query = trackSearch.toLowerCase().trim();
                          const renderOption = (track) => (
                            <TouchableOpacity
                              key={track.name}
                              style={{
                                padding: 10,
                                borderBottomWidth: 1,
                                borderBottomColor: '#1e2025',
                                backgroundColor: selectedTracks[i] === track.name ? '#2a2f36' : 'transparent',
                              }}
                              onPress={() => {
                                setSelectedTracks(prev => {
                                  const next = [...prev];
                                  next[i] = track.name;
                                  return next;
                                });
                                setTrackDropdownOpen(-1);
                                setTrackSearch('');
                              }}
                            >
                              <Text style={{ color: selectedTracks[i] === track.name ? '#ffd166' : '#c9d1d9', fontSize: 13 }}>
                                {track.name}
                              </Text>
                              <Text style={{ color: '#666', fontSize: 11 }}>{track.lengthMiles} mi</Text>
                            </TouchableOpacity>
                          );

                          if (query) {
                            return gp.tracks
                              .filter(t => t.name.toLowerCase().includes(query))
                              .map(renderOption);
                          }

                          const CATEGORIES = [
                            { label: 'Road Course', type: 'road' },
                            { label: 'Ovals', type: 'oval' },
                            { label: 'Rally Tracks', type: 'rally' },
                          ];
                          const BUCKETS = [
                            { label: 'Short (under 2 mi)', test: (mi) => mi < 2 },
                            { label: 'Medium (2 to 4 mi)', test: (mi) => mi >= 2 && mi < 4 },
                            { label: 'Long (4+ mi)', test: (mi) => mi >= 4 },
                          ];

                          return CATEGORIES.map(({ label: catLabel, type }) => {
                            const inCat = gp.tracks.filter(t => (t.type || 'road') === type);
                            if (inCat.length === 0) return null;
                            return (
                              <View key={catLabel}>
                                <Text style={{ color: '#ffd166', fontSize: 12, fontWeight: '700', paddingHorizontal: 10, paddingTop: 12, paddingBottom: 4, backgroundColor: '#0d0f12', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  {catLabel}
                                </Text>
                                {BUCKETS.map(({ label: bucketLabel, test }) => {
                                  const inBucket = inCat.filter(t => test(t.lengthMiles));
                                  if (inBucket.length === 0) return null;
                                  return (
                                    <View key={bucketLabel}>
                                      <Text style={{ color: '#888', fontSize: 10, paddingHorizontal: 10, paddingTop: 6, paddingBottom: 2 }}>
                                        {bucketLabel}
                                      </Text>
                                      {inBucket.map(renderOption)}
                                    </View>
                                  );
                                })}
                              </View>
                            );
                          });
                        })()}
                      </ScrollView>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* Advanced GT7 Lobby Settings — host overrides on top of the
            competitive preset defaults. Visible to all event creators
            (admins, hosts, racers); collapsed by default to keep the
            casual create flow clean. Lobby settings get extracted via
            extractLobbyValues() and stored on the event doc. */}
        {game === 'gt7' && gp?.type === 'racing' && (
          <View style={styles.panel}>
            <TouchableOpacity
              onPress={() => setAdvancedSettingsOpen(v => !v)}
              style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Text style={styles.h2}>Advanced Settings (Optional)</Text>
              <Ionicons
                name={advancedSettingsOpen ? 'chevron-up' : 'chevron-down'}
                size={18}
                color="#FCD000"
              />
            </TouchableOpacity>
            {!advancedSettingsOpen && (
              <Text style={styles.note}>
                Defaults match CritCash's competitive preset. Tap to customize race
                conditions, regulations, and assists for this event.
              </Text>
            )}
            {advancedSettingsOpen && (
              <View style={{ marginTop: 8 }}>
                {Object.entries(GT7_LOBBY_SETTINGS).map(([sectionKey, sectionData]) => {
                  const sectionOpen = !!advancedSectionOpen[sectionKey];
                  const sectionLabel = GT7_SETTING_SECTION_LABELS[sectionKey] || sectionKey;
                  return (
                    <View key={sectionKey} style={{ marginTop: 8, borderTopWidth: 1, borderTopColor: '#1a1e35' }}>
                      <TouchableOpacity
                        onPress={() => setAdvancedSectionOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                        style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10 }}
                      >
                        <Text style={{ color: '#FCD000', fontSize: 13, fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                          {sectionLabel}
                        </Text>
                        <Ionicons
                          name={sectionOpen ? 'chevron-up' : 'chevron-down'}
                          size={14}
                          color="#9aa0b4"
                        />
                      </TouchableOpacity>
                      {sectionOpen && Object.entries(sectionData).map(([settingKey, settingDef]) => {
                        const overrideVal = lobbySettingsOverrides?.[sectionKey]?.[settingKey];
                        const currentValue = overrideVal !== undefined ? overrideVal : settingDef.value;
                        // Check `requires` constraint — disable if sibling setting doesn't match.
                        let requiresUnmet = false;
                        if (settingDef.requires) {
                          for (const [depKey, depVal] of Object.entries(settingDef.requires)) {
                            const depOverride = lobbySettingsOverrides?.[sectionKey]?.[depKey];
                            const depCurrent = depOverride !== undefined
                              ? depOverride
                              : sectionData?.[depKey]?.value;
                            if (depCurrent !== depVal) { requiresUnmet = true; break; }
                          }
                        }
                        const isDisabled = !!settingDef.locked || requiresUnmet;
                        const useDropdown = (settingDef.options || []).length >= 5;
                        const dropdownKey = `${sectionKey}.${settingKey}`;
                        const isDropdownOpen = openSettingDropdown === dropdownKey;
                        return (
                          <View key={settingKey} style={{ paddingVertical: 8, paddingLeft: 4 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                              <Text style={{ color: '#c9d1d9', fontSize: 12, flex: 1, fontWeight: '600' }}>
                                {settingDef.label}
                                {settingDef.locked && (
                                  <Text style={{ color: '#9aa0b4', fontSize: 10, fontWeight: '500' }}> · locked</Text>
                                )}
                                {requiresUnmet && !settingDef.locked && (
                                  <Text style={{ color: '#9aa0b4', fontSize: 10, fontWeight: '500' }}> · n/a</Text>
                                )}
                              </Text>
                            </View>
                            {settingDef.description && (
                              <Text style={{ color: '#666', fontSize: 11, marginBottom: 4 }}>
                                {settingDef.description}
                              </Text>
                            )}
                            {settingDef.multiSelect ? (
                              // Multi-select pills. Value is a comma-joined
                              // string (e.g. "Soft, Medium"). 'All' is mutually
                              // exclusive with specific types; leaving nothing
                              // selected falls back to 'All' so there's always
                              // a meaningful value.
                              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                                {(() => {
                                  const selected = new Set(
                                    String(currentValue || '')
                                      .split(',')
                                      .map(s => s.trim())
                                      .filter(Boolean)
                                  );
                                  return (settingDef.options || []).map(opt => {
                                    const isActive = selected.has(opt);
                                    return (
                                      <Pill
                                        key={opt}
                                        label={opt}
                                        active={isActive}
                                        disabled={isDisabled}
                                        onPress={() => {
                                          let next;
                                          if (opt === 'All') {
                                            next = isActive ? 'All' : 'All';
                                          } else if (isActive) {
                                            const reduced = new Set(selected);
                                            reduced.delete(opt);
                                            reduced.delete('All');
                                            next = reduced.size === 0
                                              ? 'All'
                                              : (settingDef.options || []).filter(o => reduced.has(o)).join(', ');
                                          } else {
                                            const added = new Set(selected);
                                            added.delete('All');
                                            added.add(opt);
                                            next = (settingDef.options || []).filter(o => added.has(o)).join(', ');
                                          }
                                          setLobbySettingsOverrides(prev => ({
                                            ...prev,
                                            [sectionKey]: { ...(prev[sectionKey] || {}), [settingKey]: next },
                                          }));
                                        }}
                                      />
                                    );
                                  });
                                })()}
                              </View>
                            ) : useDropdown ? (
                              <View>
                                <TouchableOpacity
                                  disabled={isDisabled}
                                  onPress={() => setOpenSettingDropdown(isDropdownOpen ? '' : dropdownKey)}
                                  style={{
                                    backgroundColor: isDisabled ? '#0a0c12' : '#1b1e24',
                                    borderRadius: 6,
                                    borderWidth: 1,
                                    borderColor: '#2a2f36',
                                    paddingVertical: 8,
                                    paddingHorizontal: 10,
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    opacity: isDisabled ? 0.5 : 1,
                                  }}
                                >
                                  <Text style={{ color: isDisabled ? '#666' : '#fff', fontSize: 12 }}>
                                    {currentValue}
                                  </Text>
                                  <Ionicons
                                    name={isDropdownOpen ? 'chevron-up' : 'chevron-down'}
                                    size={14}
                                    color="#888"
                                  />
                                </TouchableOpacity>
                                {isDropdownOpen && !isDisabled && (
                                  <View style={{ backgroundColor: '#1b1e24', borderRadius: 6, borderWidth: 1, borderColor: '#2a2f36', marginTop: 4, maxHeight: 240 }}>
                                    <ScrollView nestedScrollEnabled>
                                      {(settingDef.options || []).map(opt => (
                                        <TouchableOpacity
                                          key={opt}
                                          style={{
                                            paddingVertical: 8,
                                            paddingHorizontal: 10,
                                            borderBottomWidth: 1,
                                            borderBottomColor: '#1e2025',
                                            backgroundColor: currentValue === opt ? '#2a2f36' : 'transparent',
                                          }}
                                          onPress={() => {
                                            setLobbySettingsOverrides(prev => ({
                                              ...prev,
                                              [sectionKey]: { ...(prev[sectionKey] || {}), [settingKey]: opt },
                                            }));
                                            setOpenSettingDropdown('');
                                          }}
                                        >
                                          <Text style={{ color: currentValue === opt ? '#FCD000' : '#c9d1d9', fontSize: 12 }}>
                                            {opt}
                                          </Text>
                                        </TouchableOpacity>
                                      ))}
                                    </ScrollView>
                                  </View>
                                )}
                              </View>
                            ) : (
                              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                                {(settingDef.options || []).map(opt => (
                                  <Pill
                                    key={opt}
                                    label={opt}
                                    active={currentValue === opt}
                                    disabled={isDisabled}
                                    onPress={() => {
                                      setLobbySettingsOverrides(prev => ({
                                        ...prev,
                                        [sectionKey]: { ...(prev[sectionKey] || {}), [settingKey]: opt },
                                      }));
                                    }}
                                  />
                                ))}
                              </View>
                            )}
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
                {Object.keys(lobbySettingsOverrides).length > 0 && (
                  <TouchableOpacity
                    onPress={() => setLobbySettingsOverrides({})}
                    style={{ marginTop: 12, paddingVertical: 8, alignItems: 'center', borderRadius: 6, backgroundColor: '#1a1e35' }}
                  >
                    <Text style={{ color: '#FCD000', fontSize: 12, fontWeight: '700' }}>Reset all to defaults</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        )}

        {/* Team Size Selection - shown whenever the format offers more than
            one size, INCLUDING H2H (two sides of any size the format allows).
            Racing/Showdown never reach here (availableTeamSizes is [1]). */}
        {availableTeamSizes.length > 1 && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Team Size</Text>
            <View style={styles.pillRow}>
              {availableTeamSizes.map((size) => (
                <Pill
                  key={size}
                  label={size === 1 ? 'Solo' : `${size}v${size}`}
                  active={teamSize === size}
                  onPress={() => setTeamSize(size)}
                />
              ))}
            </View>
            <Text style={styles.note}>
              {teamSize === 1 
                ? 'Players compete individually'
                : `Teams of ${teamSize} players compete together`}
            </Text>
            
            {/* Team Formation Explanation - Only show for team modes */}
            {teamSize > 1 && (
              <View style={styles.teamInfoBox}>
                <Ionicons name="people" size={18} color="#9146FF" style={{ marginRight: 8 }} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.teamInfoTitle}>Team Entry Options</Text>
                  <Text style={styles.teamInfoText}>
                    Players can join solo and get matched into pickup teams, or enter with friends as a pre-formed team.
                  </Text>
                </View>
              </View>
            )}
          </View>
        )}
        
        {/* Your Role (COD/CDL host model) — shown for team events 3v3+, both
            brackets and team H2H. The creator hosts (casts, doesn't play) or
            plays as a participant. Bracket hosts pick which rounds to host —
            one match per round; other matches go to other hosts. H2H is one
            match, so there is nothing to pick (auto round 1). 1v1/2v2 never
            reach here (hostEligible thresholds on hostMinTeamSize). */}
        {hostEligible && (isBracket || isH2H) && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Your Role</Text>
            <View style={styles.pillRow}>
              <Pill
                label="Host (cast, don't play)"
                active={creatorRole === 'host'}
                onPress={() => setCreatorRole('host')}
              />
              <Pill
                label="Participant (play)"
                active={creatorRole === 'racer'}
                onPress={() => setCreatorRole('racer')}
              />
            </View>
            <Text style={styles.note}>
              {creatorRole === 'host'
                ? (isH2H
                    ? "You'll host (cast) the match and won't play - both teams join your lobby."
                    : "You'll host (cast) matches and won't play. Pick the rounds you'll host below — one match each; other matches are run by other hosts.")
                : "You'll play on a team. Each match still needs a host (CODCaster) — other users sign up to host, and a match can't run until it has one."}
            </Text>

            {/* H2H is a single match — no round picking; the selection effect
                auto-selects round 1 for the joinEventAsHost registration. */}
            {creatorRole === 'host' && !isH2H && hostRoundsList.length > 0 && (
              <View style={{ marginTop: 14 }}>
                <Text style={[styles.note, { color: '#FCD000', fontWeight: '700', marginBottom: 6 }]}>
                  Rounds you'll host (one match each)
                </Text>
                <View style={styles.pillRow}>
                  {hostRoundsList.map((r) => (
                    <Pill
                      key={r.round}
                      label={`${r.label} · ${r.matches} match${r.matches > 1 ? 'es' : ''}`}
                      active={hostSelectedRounds.includes(r.round)}
                      onPress={() => toggleHostRound(r.round)}
                    />
                  ))}
                </View>
                <Text style={styles.note}>
                  {hostSelectedRounds.length === 0
                    ? 'Select at least one round to host.'
                    : `You'll host one match in each of ${hostSelectedRounds.length} round${hostSelectedRounds.length > 1 ? 's' : ''}. Other matches in those rounds are hosted by other hosts.`}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Show team size info even when only one option (locked team size) */}
        {!isRacing && !isShowdown && availableTeamSizes.length === 1 && teamSize > 1 && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Team Format</Text>
            <View style={styles.lockedTeamBadge}>
              <Ionicons name="people" size={16} color="#FCD000" style={{ marginRight: 6 }} />
              <Text style={styles.lockedTeamText}>{teamSize}v{teamSize} Teams Required</Text>
            </View>
            <Text style={styles.note}>
              This format requires teams of {teamSize} players.
            </Text>
            <View style={styles.teamInfoBox}>
              <Ionicons name="shuffle" size={18} color="#9146FF" style={{ marginRight: 8 }} />
              <View style={{ flex: 1 }}>
                <Text style={styles.teamInfoTitle}>Pickup Groups Enabled</Text>
                <Text style={styles.teamInfoText}>
                  Solo players will be automatically matched into teams at event start based on registration order.
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Bracket/Heat Size - Hide for H2H and Showdown */}
        {!isH2H && !isShowdown && (
          <View style={styles.panel}>
            <Text style={styles.h2}>
              {isRacing 
                ? 'Number of Heats' 
                : 'Bracket Size (# of teams)'}
            </Text>
            <View style={[styles.pillRow, { flexWrap: 'wrap' }]}>
              {(isRacing ? heatSizeOptions : bracketSizeOptions).map((sz) => (
                <Pill
                  key={sz}
                  label={fmtSize(sz)}
                  active={isRacing ? heatCount === sz : bracketSize === sz}
                  onPress={() => (isRacing ? setHeatCount(sz) : setBracketSize(sz))}
                />
              ))}
            </View>
            <Text style={styles.note}>
              {isRacing
                ? `${heatCount} heats x ${effectiveGridSize} racers = ${entriesExpected} total slots`
                : `${bracketSize} teams = ${Math.floor(bracketSize / 2)} first-round matches (${totalPlayers} total players)`}
            </Text>
            {/* Crits OFF: sizes above 16 are a member feature — in-place
                upgrade funnel (leaving the form = abandoned cart). */}
            {sizeNeedsMembership && (
              <TouchableOpacity onPress={() => setShowUpgradeModal(true)} activeOpacity={0.8}>
                <View style={{ marginTop: 10, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                  <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                    Sizes above 16 {isRacing ? 'heats' : 'teams'} are included with paid plans
                  </Text>
                  <Text style={[styles.note, { fontSize: 11, marginTop: 2 }]}>
                    Pick a size up to 16, or tap here to choose a plan.
                  </Text>
                </View>
              </TouchableOpacity>
            )}
            {/* Ladder summary for paid sizes: the unlock fee is the creator's
                buy-in — counts toward their entry and seeds the prize pool. */}
            {critsEnabled && !isAdmin && (() => {
              const selSize = isRacing ? heatCount : bracketSize;
              const tier = sizeTierUI(selSize);
              if (!tier.unlockFee) return null;
              return (
                <View style={{ marginTop: 10, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                  <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                    {fmtSize(selSize)} {isRacing ? 'heats' : 'teams'} · Unlock: {tier.unlockFee} Crits
                  </Text>
                  <Text style={[styles.note, { fontSize: 11, marginTop: 2 }]}>
                    The unlock counts as your entry ({tier.unlockFee} Crits, seeds the Crit pool)
                    and this size requires a minimum entry of {tier.minEntry} Crits per {entryUnit}.
                  </Text>
                </View>
              );
            })()}
          </View>
        )}

        {/* Entry Cost (Crits) — hidden while Crits are OFF (entry is 0) */}
        {critsEnabled && (
        <View style={styles.panel}>
          <Text style={styles.h2}>Entry Cost (Crits, per {entryUnit})</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Crits</Text>
              <TextInput
                style={styles.input}
                keyboardType="number-pad"
                placeholder="1"
                placeholderTextColor="#666"
                value={critCostInput}
                onChangeText={(t) => setCritCostInput(t.replace(/[^0-9]/g, ''))}
              />
            </View>
          </View>
          <Text style={styles.note}>
            Players spend {critEntryCost} Crits to enter this event.
          </Text>
          {entryBelowMin && (
            <Text style={[styles.note, { color: '#ff6b6b', marginTop: 4 }]}>
              A {fmtSize(selectedLadderSize)}-size event requires an entry of at
              least {ladderMinEntry} Crits per {entryUnit}.
            </Text>
          )}
        </View>
        )}

        {/* Custom Event Title — free for Pro/Elite (membership perk), 5 Crits
            for Rookie (Crit sink + upgrade nudge); admins free. Server decides
            the fee from subscriptionTier. Server-moderated: profanity,
            game-name licensing, brand/trademark (Claude pass). Rejection
            blocks creation with nothing charged. */}
        <View style={styles.panel}>
          <Text style={styles.h2}>Custom Event Title{isAdmin ? ' (Admin — free)' : ''}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <TouchableOpacity
              onPress={() => setUseCustomTitle(!useCustomTitle)}
              style={[styles.checkbox, useCustomTitle && styles.checkboxActive]}
            >
              {useCustomTitle && <Text style={styles.checkmark}></Text>}
            </TouchableOpacity>
            <Text style={[styles.label, { marginLeft: 8, marginBottom: 0 }]}>
              Use Custom Title
            </Text>
          </View>
          {useCustomTitle && (
            <>
              <TextInput
                style={styles.input}
                placeholder="Enter custom event title..."
                placeholderTextColor="#666"
                maxLength={80}
                value={customEventTitle}
                onChangeText={setCustomEventTitle}
              />
              <Text style={styles.note}>
                Replaces the auto-generated event and stream title.
                {!isAdmin && (critsEnabled
                  ? ' Free for Pro & Elite members · 5 Crits for Rookie (added to your Crit pool). Titles are reviewed — no brand names, full game names, or offensive language. If a title isn’t approved, nothing is charged.'
                  : ' Titles are reviewed — no brand names, full game names, or offensive language.')}
              </Text>
            </>
          )}
        </View>

        {/* Start Time */}
        <View style={styles.panel}>
          <Text style={styles.h2}>Start Time</Text>
          <View style={styles.pillRow}>
            <Pill label="Relative" active={startMode === 'relative'} onPress={() => setStartMode('relative')} />
            <Pill label="Absolute" active={startMode === 'absolute'} onPress={() => setStartMode('absolute')} />
            <Pill label="Open Until Full" active={startMode === 'fill'} onPress={() => setStartMode('fill')} />
          </View>

          {startMode === 'relative' && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.label}>Start in (minutes from now)</Text>
              <View style={styles.pillRow}>
                {[30, 60, 120].map((m) => (
                  <Pill
                    key={m}
                    label={m >= 60 ? `${m / 60}h` : `${m}m`}
                    active={startInMins === m}
                    onPress={() => setStartInMins(m)}
                  />
                ))}
              </View>
              <Text style={[styles.note, { marginTop: 6 }]}>
                Registration closes 15 minutes before start.
              </Text>
            </View>
          )}

          {startMode === 'absolute' && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.label}>Pick date & time</Text>
              {DatePicker ? (
                <DatePicker
                  value={absDate}
                  onChange={setAbsDate}
                  min={minStart}
                  max={maxStart}
                  style={{
                    backgroundColor: '#0f1115',
                    color: COLORS.GOLD || '#ffd166',
                    borderColor: '#2a2f36',
                    borderWidth: 1,
                    borderRadius: 10,
                    padding: 12,
                    width: '100%',
                  }}
                />
              ) : (
                <Text style={styles.note}>
                  Pick a time using your native date/time control (implement as needed).
                </Text>
              )}
              <Text style={[styles.note, { marginTop: 6 }]}>
                Must be at least 1 hour from now. Window: {isAdmin ? 'up to 30 days' : 'up to 24 hours'}.
                Registration closes 15 minutes before start.
              </Text>
            </View>
          )}

          {startMode === 'fill' && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.label}>Start (minutes after event fills)</Text>
              <View style={styles.pillRow}>
                {[15, 20, 30].map((m) => (
                  <Pill
                    key={m}
                    label={`${m}m`}
                    active={fillDelayMins === m}
                    onPress={() => setFillDelayMins(m)}
                  />
                ))}
              </View>
              <Text style={[styles.note, { marginTop: 6 }]}>
                The event locks the moment it fills. Everyone is notified and the
                start timer kicks off. No cancellations after lock.
              </Text>
            </View>
          )}
        </View>

        {/* Time Between Rounds — only for multi-round events (heats/brackets) */}
        {isMultiRound && (
          <View style={styles.panel}>
            <Text style={styles.h2}>Time Between Rounds</Text>
            <Text style={styles.note}>
              How long players get between rounds — a quick setup/bio break, or a longer
              gap for a multi-day event people can plan to tune in and watch. Default 5m.
            </Text>
            <View style={[styles.pillRow, { flexWrap: 'wrap', marginTop: 12 }]}>
              {GAP_STEPS.map((s) => (
                <Pill
                  key={s.min}
                  label={s.label}
                  active={interRoundGapMinutes === s.min}
                  onPress={() => setInterRoundGapMinutes(s.min)}
                />
              ))}
            </View>

            {startBaseMs ? (
              <View style={{ marginTop: 12, padding: 10, backgroundColor: '#0d0f12', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166' }}>
                <Text style={[styles.note, { color: '#ffd166', fontWeight: '700' }]}>
                  Projected Schedule ({projectedRounds} rounds)
                </Text>
                {projectedSchedule.map((rs) => (
                  <Text key={rs.round} style={[styles.note, { fontSize: 12, marginTop: 2 }]}>
                    {projectedRoundLabel(rs.round, projectedRounds)}: ~{fmtWhen(rs.startMs)}
                  </Text>
                ))}
                <Text style={[styles.note, { fontSize: 12, marginTop: 6, color: '#fff', fontWeight: '700' }]}>
                  Est. event ends ~{fmtWhen(projectedEndMs)} · {projectedSpanLabel} total
                </Text>
                <Text style={[styles.note, { fontSize: 10, color: '#888', marginTop: 2 }]}>
                  {isDayAnchored
                    ? `Every round starts at the same time of day as Round 1 (~${Math.round(perRoundMinutes)} min/round).`
                    : `Rough estimate (~${Math.round(perRoundMinutes)} min/round). Actual round starts depend on when each round's matches finish.`}
                </Text>
              </View>
            ) : (
              <Text style={[styles.note, { marginTop: 8, fontStyle: 'italic', color: '#888' }]}>
                {startMode === 'fill'
                  ? 'The projected schedule appears once the event fills (Open Until Full).'
                  : 'Pick a start time above to see the projected round schedule and end time.'}
              </Text>
            )}
          </View>
        )}

        {/* Optional GT7 H2H Room ID submission */}
        {isH2H && game === 'gt7' && (
          <View style={styles.panel}>
            <Text style={styles.h2}>GT7 Room ID (Advanced)</Text>
            <Text style={[styles.note, { marginTop: 4 }]}>
              Optional. Create the GT7 lobby now and paste the Room ID here. Your opponent
              will get the code the moment they join — no host modal interrupting the match.
            </Text>
            <Text style={[styles.note, { marginTop: 4, color: '#FF9800' }]}>
              ⚠️ Your GT7 lobby must stay open until your opponent joins. If you leave it
              blank, you'll be prompted to submit a Room ID once the match seeds.
            </Text>
            <TextInput
              style={[styles.input, { marginTop: 10, letterSpacing: 4, textAlign: 'center', fontSize: 18 }]}
              placeholder="e.g. souij4"
              placeholderTextColor="#666"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={6}
              value={creatorRoomId}
              onChangeText={(t) => setCreatorRoomId(t.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().slice(0, 6))}
            />
            {creatorRoomId && creatorRoomId.length !== 6 && (
              <Text style={[styles.note, { marginTop: 6, color: '#e74c3c' }]}>
                Room ID must be exactly 6 characters (a-z, 0-9).
              </Text>
            )}
          </View>
        )}

        {/* Preview */}
        <View style={[styles.panel, styles.preview]}>
          <Text style={styles.h2}>Preview</Text>
          
          {/* Event Name */}
          <View style={{ marginTop: 8, marginBottom: 12 }}>
            <Text style={styles.note}>Event Name (displayed in listings)</Text>
            <Text style={[styles.title, { fontSize: 16, marginTop: 4 }]}>{eventName}</Text>
          </View>

          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Mode</Text>
            <Text style={styles.previewValue}>
              {isH2H ? `Head-to-Head (${teamSize > 1 ? `${teamSize}v${teamSize}` : '1v1'})` : isShowdown ? (isBattleRoyale ? 'Battle Royale' : 'Showdown') : isBracket ? 'Tournament Bracket' : 'Racing Heats'}
            </Text>
          </View>

          {/* Team H2H capacity: two sides x teamSize players */}
          {isH2H && teamSize > 1 && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Entrants</Text>
              <Text style={styles.previewValue}>2 teams - {totalPlayers} players</Text>
            </View>
          )}

          {!isH2H && !isShowdown && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>{isRacing ? 'Heats' : 'Bracket Size'}</Text>
                <Text style={styles.previewValue}>{isRacing ? heatCount : bracketSize}</Text>
              </View>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Max Entrants</Text>
                <Text style={styles.previewValue}>{entrants} {entryUnit}s</Text>
              </View>
              {teamSize > 1 && (
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>Total Players</Text>
                  <Text style={styles.previewValue}>{totalPlayers} players</Text>
                </View>
              )}
            </>
          )}
          
          {isShowdown && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Max Entrants</Text>
              <Text style={styles.previewValue}>{entrants} {entryUnit}s</Text>
            </View>
          )}

          {isBattleRoyale && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Squad Size</Text>
                <Text style={styles.previewValue}>{SQUAD_SIZE_LABELS[squadSize] || 'Solos'}</Text>
              </View>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Min to Start</Text>
                <Text style={styles.previewValue}>{brMinPlayers} players</Text>
              </View>
            </>
          )}
          
          {critsEnabled && (
          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Entry Cost</Text>
            <Text style={styles.previewValue}>{critEntryCost} Crits per {entryUnit}</Text>
          </View>
          )}
          {critsEnabled && !isAdmin && selectedLadderSize > 0 && sizeTierUI(selectedLadderSize).unlockFee > 0 && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Size Unlock (your entry)</Text>
              <Text style={styles.previewValue}>{sizeTierUI(selectedLadderSize).unlockFee} Crits → Crit pool</Text>
            </View>
          )}
          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Platform</Text>
            <Text style={styles.previewValue}>{platformStr} {crossplay && '(Crossplay)'}</Text>
          </View>
          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Visibility</Text>
            <Text style={styles.previewValue}>{visibility === 'public' ? 'Public' : 'Private'}</Text>
          </View>

          {/* GT7-specific preview rows */}
          {gp?.roomSettings?.common?.carCategory && (isRacing || isShowdown || isH2H) && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Vehicle Category</Text>
              <Text style={styles.previewValue}>
                {gp.roomSettings.common.carCategory.options.find(o => o.key === selectedCarCategory)?.label || selectedCarCategory}
              </Text>
            </View>
          )}
          {gp?.type === 'racing' && selectedTracks.filter(Boolean).length > 0 && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>{selectedTracks.filter(Boolean).length === 1 ? 'Track' : 'Tracks'}</Text>
                <Text style={styles.previewValue}>{selectedTracks.filter(Boolean).length} selected</Text>
              </View>
              {selectedTracks.map((t, i) => t ? (
                <Text key={i} style={[styles.previewLine, { marginLeft: 8, fontSize: 11 }]}>
                  {isRacing ? (i === totalTrackRounds - 1 ? 'Finals' : `R${i + 1}`) : ''}: {t}
                </Text>
              ) : null)}
            </>
          )}
          {game === 'mdn' && Object.entries(MADDEN_GAME_SETTINGS).map(([key, def]) => (
            <View key={key} style={styles.previewRow}>
              <Text style={styles.previewLabel}>{def.label}</Text>
              <Text style={styles.previewValue}>
                {def.type === 'minutes' ? `${maddenSettings[key]} Min` : maddenSettings[key]}
              </Text>
            </View>
          ))}
          {/* Show the host's CONFIGURED race shape (Race Configuration panel),
              not the format preset — the preset is just the slider's default. */}
          {gp?.formats?.[formatName]?.laps && !isEndurance && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Laps</Text>
              <Text style={styles.previewValue}>{lapsOverride}</Text>
            </View>
          )}
          {gp?.formats?.[formatName]?.laps && isEndurance && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Endurance</Text>
              <Text style={styles.previewValue}>{enduranceMinutes} Min</Text>
            </View>
          )}
          {gp?.formats?.[formatName]?.minPitStops !== undefined && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Min. Pit Stops</Text>
              <Text style={styles.previewValue}>{pitStopsOverride}</Text>
            </View>
          )}
          {game === 'gt7' && (
            <View style={styles.previewRow}>
              <Text style={styles.previewLabel}>Room Mode</Text>
              <Text style={styles.previewValue}>Practice/Qualifier/Race</Text>
            </View>
          )}

          {critsEnabled && (
          <>
          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Crit Pool (if full)</Text>
            <Text style={styles.previewValue}>{fmtCrits(live.grossPoolCents)}</Text>
          </View>
          <View style={styles.previewRow}>
            <Text style={styles.previewLabel}>Paid to finishers</Text>
            <Text style={styles.previewValue}>{fmtCrits(live.netPoolCents)}</Text>
          </View>
          </>
          )}

          {!isH2H && isBracket && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Rounds to Final</Text>
                <Text style={styles.previewValue}>{roundsToFinal}</Text>
              </View>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Rounds to Champion</Text>
                <Text style={styles.previewValue}>{roundsToChampion}</Text>
              </View>
            </>
          )}

          {isRacing && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Rounds to Final</Text>
                <Text style={styles.previewValue}>{heatRoundsToFinal}</Text>
              </View>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Rounds to Champion</Text>
                <Text style={styles.previewValue}>{heatRoundsToChampion}</Text>
              </View>
            </>
          )}

          {isMultiRound && (
            <>
              <View style={styles.previewRow}>
                <Text style={styles.previewLabel}>Time Between Rounds</Text>
                <Text style={styles.previewValue}>
                  {(GAP_STEPS.find((s) => s.min === interRoundGapMinutes) || {}).label || `${interRoundGapMinutes}m`}
                </Text>
              </View>
              {projectedEndMs && (
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>Est. Event End</Text>
                  <Text style={styles.previewValue}>
                    ~{fmtWhen(projectedEndMs)} ({projectedSpanLabel})
                  </Text>
                </View>
              )}
            </>
          )}

          {/* Streaming Title — HIDDEN 2026-07-12 (founder): abandoned since
              streaming moved natively to CritCash (per-user stream keys, no
              external title matching). Still stored on the event
              (requiredStreamTitle) for potential future marketing use — e.g.
              "earn Crits streaming with this title on Twitch/YouTube",
              verified via their OAuth APIs. Do not delete. */}

          {critsEnabled && Array.isArray(live.stageBands) && live.stageBands.length > 0 && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.note}>
                {isH2H ? 'Winner Payout' : 'Top Payouts (if event fills)'}
              </Text>
              
              {/* Add explanatory text for racing events */}
              {isRacing && heatCount > 1 && (
                <Text style={[styles.note, { marginTop: 4, marginBottom: 8, fontSize: 10, lineHeight: 14 }]}>
                  Top {Math.floor(gridSize / 2)} from each heat advance{hostRacesActive ? ` (you race too — ${effectiveGridSize}-car grid)` : ''}.
                </Text>
              )}

              {/* BR squads: bands are per-SQUAD placement shares */}
              {isBattleRoyale && squadSize > 1 && (
                <Text style={[styles.note, { marginTop: 4, marginBottom: 8, fontSize: 10, lineHeight: 14 }]}>
                  Squad placements: each amount is that squad's share, split evenly between its players.
                </Text>
              )}
              
              {isH2H ? (
                // H2H: Simple winner takes all — fmtCrits like every other
                // mode; band.total is the PRE-FORMATTED DOLLAR string, which
                // rendered a 9-Crit pool as "$0.09".
                <Text style={styles.previewLine}>
                  Winner's prize: {fmtCrits(live.stageBands[0]?.totalCents)}
                </Text>
              ) : isBattleRoyale ? (
                // Battle royale: flat static wz bands, one row per placement
                // (squads: per-squad placement share - see note above).
                live.stageBands.map((band, i) => (
                  <Text key={i} style={styles.previewLine}>
                    {band.stage} (#{band.from}): {fmtCrits(band.totalCents)}
                  </Text>
                ))
              ) : (isRacing || isShowdown) ? (
                // Racing/Showdown: Group bands by stage for better readability
                (() => {
                  // Group bands by their parent stage (Finals, Heat of 2, Heat of 4, etc.)
                  const groupedBands = {};
                  live.stageBands.forEach(band => {
                    // Extract parent stage from band.stage (e.g., "Heat of 2 - Near Miss" -> "Heat of 2")
                    const dashIndex = band.stage.indexOf(' - ');
                    const parentStage = dashIndex > -1 ? band.stage.substring(0, dashIndex) : 'Finals';
                    const tierName = dashIndex > -1 ? band.stage.substring(dashIndex + 3) : band.stage;
                    
                    if (!groupedBands[parentStage]) {
                      groupedBands[parentStage] = [];
                    }
                    groupedBands[parentStage].push({ ...band, tierName });
                  });
                  
                  // Define display order and friendly names
                  const stageOrder = ['Finals', 'Heat of 2', 'Heat of 4', 'Heat of 8', 'Heat of 16', 'Heat of 32', 'Heat of 64'];
                  const stageLabels = {
                    'Finals': 'Finals',
                    'Heat of 2': 'Semifinal Round',
                    'Heat of 4': 'Quarterfinal Round',
                    'Heat of 8': 'Round of 8',
                    'Heat of 16': 'Round of 16',
                    'Heat of 32': 'Round of 32',
                    'Heat of 64': 'Round of 64',
                  };
                  
                  // Sort stages in display order
                  const sortedStages = Object.keys(groupedBands).sort((a, b) => {
                    const aIdx = stageOrder.indexOf(a);
                    const bIdx = stageOrder.indexOf(b);
                    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
                  });
                  
                  return sortedStages.map((stage, stageIdx) => (
                    <View key={stage} style={{ marginTop: stageIdx > 0 ? 10 : 4 }}>
                      <Text style={[styles.previewLine, { fontWeight: '700', color: stage === 'Finals' ? '#ffd166' : '#9aa0a6', fontSize: 12 }]}>
                        {stageLabels[stage] || stage}
                      </Text>
                      {groupedBands[stage].map((band, i) => {
                        // Format position display
                        const posText = band.from === band.to 
                          ? `#${band.from}` 
                          : `#${band.from}-${band.to}`;
                        
                        // For Finals tiers, show position; for elimination rounds, show count
                        const countInfo = stage === 'Finals'
                          ? (band.count > 1 ? ` x ${band.count}` : '')
                          : ` x ${band.count} racers`;
                        
                        return (
                          <Text key={i} style={[styles.previewLine, { marginLeft: 8, fontSize: 11 }]}>
                            {band.tierName} ({posText}{countInfo}): {fmtCrits(band.totalCents)}{band.count > 1 ? ` (${fmtCrits(Math.ceil(band.totalCents / band.count))} ea)` : ''}
                          </Text>
                        );
                      })}
                    </View>
                  ));
                })()
              ) : (
                // Bracket: Show place ranges
                live.stageBands.slice(0, 100).map((band, i) => (
                  <Text key={i} style={styles.previewLine}>
                    Places #{band.from}{band.to !== band.from ? `-${band.to}` : ''}: {fmtCrits(band.totalCents)} total{band.count > 1 ? ` (${fmtCrits(Math.ceil(band.totalCents / band.count))} each)` : ''}
                  </Text>
                ))
              )}
            </View>
          )}
        </View>

        <TouchableOpacity
          disabled={createDisabled || isCreating}
          onPress={onCreate}
          style={[styles.cta, (createDisabled || isCreating) && styles.ctaDisabled]}
        >
          <Text style={styles.ctaText}>
            {isCreating
              ? 'Creating...'
              : !eligibilityLoading && !hasStreamKey
              ? 'Set Up Streaming First'
              : !eligibilityLoading && !hasIGN
                ? 'Set IGN to Create Event' 
                : createDisabled 
                  ? 'Complete fields to create' 
                  : 'Create Event'}
          </Text>
        </TouchableOpacity>

        <View style={{ height: 32 }} />
      </ScrollView>
      
      {/* Create Event Confirmation Modal */}
      <Modal visible={showCreateConfirmModal} transparent animationType="fade">
        <Pressable 
          style={styles.confirmModalBackdrop} 
          onPress={() => setShowCreateConfirmModal(false)}
        >
          <Pressable style={styles.confirmModalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.confirmModalTitle}>Create Event</Text>
            
            {/* Event Summary */}
            <View style={styles.confirmModalEventBox}>
              <Text style={styles.confirmModalEventName}>{eventName || 'New Event'}</Text>
              <Text style={styles.confirmModalEventGame}>
                {gp?.displayName || game} {mode === 'h2h' ? 'Head-to-Head' : mode === 'showdown' ? (isBattleRoyale ? 'Battle Royale' : 'Showdown') : mode === 'racing' ? 'Heats' : 'Tournament'}
              </Text>
              <Text style={styles.confirmModalEventDetails}>
                {isH2H
                  ? (teamSize > 1 ? `2 teams - ${totalPlayers} players` : '2 players')
                  : `${entriesExpected} ${entryUnit}s`}{critsEnabled ? ` Entry: ${critEntryCost} Crits` : ''}
              </Text>
            </View>
            
            {/* Team Info Summary - Only show for team events */}
            {teamSize > 1 && (
              <View style={styles.teamSummaryBox}>
                <View style={styles.teamSummaryRow}>
                  <Text style={styles.teamSummaryLabel}>Team Format</Text>
                  <Text style={[styles.teamSummaryValue, styles.teamSummaryHighlight]}>{teamSize}v{teamSize}</Text>
                </View>
                <View style={styles.teamSummaryRow}>
                  <Text style={styles.teamSummaryLabel}>{isH2H ? 'Teams' : 'Teams in Bracket'}</Text>
                  <Text style={styles.teamSummaryValue}>{entriesExpected}</Text>
                </View>
                <View style={styles.teamSummaryRow}>
                  <Text style={styles.teamSummaryLabel}>Total Players</Text>
                  <Text style={styles.teamSummaryValue}>{totalPlayers}</Text>
                </View>
                <View style={styles.teamSummaryRow}>
                  <Text style={styles.teamSummaryLabel}>Pickup Groups</Text>
                  <Text style={[styles.teamSummaryValue, { color: '#4CAF50' }]}>Enabled</Text>
                </View>
              </View>
            )}
            
            {/* Host / Racer role toggle - GT7 racing & showdown, plus Warzone
                BR (cod showdown): "Racer" reads as "Player" for shooters. */}
            {gp?.supportsHost && (isRacing || isShowdown) && (
              <View style={styles.confirmModalRoleBox}>
                <Text style={styles.confirmModalRoleLabel}>Create As</Text>
                <View style={styles.confirmModalRoleRow}>
                  <TouchableOpacity
                    style={[styles.confirmModalRolePill, creatorRole === 'racer' && styles.confirmModalRolePillActive]}
                    onPress={() => setCreatorRole('racer')}
                  >
                    <Text style={[styles.confirmModalRolePillText, creatorRole === 'racer' && styles.confirmModalRolePillTextActive]}>{gp?.type === 'racing' ? 'Racer' : 'Player'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.confirmModalRolePill, creatorRole === 'host' && styles.confirmModalRolePillActive]}
                    onPress={() => setCreatorRole('host')}
                  >
                    <Text style={[styles.confirmModalRolePillText, creatorRole === 'host' && styles.confirmModalRolePillTextActive]}>Host</Text>
                  </TouchableOpacity>
                </View>
                {/* Hosts on GT7 racing events choose: spectate (classic
                    15+host) or race too. Showdown: they take the 16th seat and
                    the full grid races. Heats: they take a normal seat in the
                    heat they run (heat sizes stay uniform). Hosted racers pay
                    the entry like every racer and keep the host reward.
                    Battle royale (founder 2026-09-02): Host & play — the
                    creator hosts the private match AND drops in, taking a
                    normal player slot inside the cap (no extra seat; the
                    spectate-only CODCaster seat is the Host-only mode). */}
                {creatorRole === 'host' && (isShowdown || isRacing) && (gp?.type === 'racing' || isBattleRoyale) && (
                  <View style={[styles.confirmModalRoleRow, { marginTop: 8 }]}>
                    <TouchableOpacity
                      style={[styles.confirmModalRolePill, !hostRaces && styles.confirmModalRolePillActive]}
                      onPress={() => setHostRaces(false)}
                    >
                      <Text style={[styles.confirmModalRolePillText, !hostRaces && styles.confirmModalRolePillTextActive]}>
                        {isBattleRoyale ? "Host only — cast, don't drop in"
                          : isShowdown ? `Host only — ${gridSize} racers + you` : 'Host only — run your heats, spectate'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.confirmModalRolePill, hostRaces && styles.confirmModalRolePillActive]}
                      onPress={() => setHostRaces(true)}
                    >
                      <Text style={[styles.confirmModalRolePillText, hostRaces && styles.confirmModalRolePillTextActive]}>
                        {isBattleRoyale ? `Host & play — you drop in too (one of ${gridSize} slots)`
                          : isShowdown ? `Host & race — full grid of ${gridSize + 1}` : 'Host & race — a seat in the heat you run'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}

            {/* Your Entry Fee — hidden for hosts; show earnings estimate instead */}
            {isHostMode ? (
              <View style={styles.confirmModalFeeBox}>
                <Text style={[styles.confirmModalFeeLabel, { color: '#4CAF50' }]}>{hostRacesActive ? 'No Fee to Create' : 'No Entry Fee'}</Text>
                <Text style={[styles.confirmModalFeeAmount, { fontSize: 16, color: '#aaa' }]}>{hostRacesActive ? `You ${isBattleRoyale ? 'play' : 'race'} too — enter from the event page${critsEnabled ? ` (${critEntryCost} Crits)` : ''}` : (isRacing || isShowdown) ? 'Hosts spectate for free' : "You host — you don't play"}</Text>
                {/* Host reward (founder 2026-07-16): 20% of each lobby's pot in
                    minted Crits per round, floor 1 / cap 25, depth multiplier up
                    to 2x. See computeHostRoundReward in constants/hostRewards.
                    Crits OFF: the host Crit mint is skipped server-side (the
                    hosting comp signal still counts), so the promise is hidden. */}
                {critsEnabled && (
                <View style={styles.confirmModalHostEarnings}>
                  <View style={styles.confirmModalHostEarningsHeader}>
                    <Ionicons name="trophy-outline" size={14} color="#4CAF50" />
                    <Text style={styles.confirmModalHostEarningsTitle}>Host Reward</Text>
                  </View>
                  <Text style={styles.confirmModalHostEarningsAmount}>20% of the lobby's Crit pool</Text>
                  <Text style={styles.confirmModalHostEarningsNote}>
                    In Crits, each round you host (min 1). Grows up to 2x the more rounds you run.
                  </Text>
                </View>
                )}
              </View>
            ) : !critsEnabled ? (
              <View style={styles.confirmModalFeeBox}>
                <Text style={[styles.confirmModalFeeLabel, { color: '#4CAF50' }]}>Free to Create</Text>
              </View>
            ) : (
              <View style={styles.confirmModalFeeBox}>
                <Text style={styles.confirmModalFeeLabel}>Your Cost to Create</Text>
                <Text style={styles.confirmModalFeeAmount}>{creatorTotalCrits} Crits</Text>
                {/* Itemized so the charge is never a surprise: the size unlock
                    REPLACES the entry when larger (max, not sum); title fee adds. */}
                {creatorUnlockFee > critEntryCost ? (
                  <Text style={styles.confirmModalFeeBreakdown}>
                    Size unlock ({fmtSize(selectedLadderSize)} {isRacing ? 'heats' : 'teams'}): {creatorUnlockFee} Crits — counts as your entry, seeds the Crit pool
                  </Text>
                ) : (
                  <Text style={styles.confirmModalFeeBreakdown}>
                    Your entry: {critEntryCost} Crits
                  </Text>
                )}
                {creatorTitleFee > 0 && (
                  <Text style={styles.confirmModalFeeBreakdown}>
                    Custom title: +{creatorTitleFee} Crits (added to the Crit pool)
                  </Text>
                )}
                {useCustomTitle && customEventTitle.trim() && creatorTitleFee === 0 && !isAdmin && (
                  <Text style={[styles.confirmModalFeeBreakdown, { color: '#4CAF50' }]}>
                    Custom title: Free on your plan
                  </Text>
                )}
                <Text style={styles.confirmModalFeeBreakdown}>
                  Players joining pay {critEntryCost} Crits{teamSize > 1 ? ' each (per player)' : ' each'}.
                </Text>
              </View>
            )}

            {/* Payment Breakdown - cash gift balance (dormant; Crit events skip) */}
            {!isHostMode && entryFeeCents > 0 && giftBalanceCents > 0 && (
              <View style={styles.confirmModalPaymentBox}>
                <Text style={styles.confirmModalPaymentTitle}>Payment Breakdown</Text>

                {/* How much from gift balance */}
                <View style={styles.confirmModalPaymentRow}>
                  <Text style={styles.confirmModalPaymentLabel}>
                    From Gift Card Balance:
                  </Text>
                  <Text style={[styles.confirmModalPaymentValue, { color: '#E91E63' }]}>
                    {fmtUSD(Math.min(giftBalanceCents, entryFeeCents))}
                  </Text>
                </View>

                {/* How much from wallet (if needed) */}
                {entryFeeCents > giftBalanceCents && (
                  <View style={styles.confirmModalPaymentRow}>
                    <Text style={styles.confirmModalPaymentLabel}>
                      From Wallet Balance:
                    </Text>
                    <Text style={[styles.confirmModalPaymentValue, { color: '#2ECC71' }]}>
                      {fmtUSD(entryFeeCents - giftBalanceCents)}
                    </Text>
                  </View>
                )}

                <Text style={styles.confirmModalPaymentNote}>
                  Gift card balance is always used first
                </Text>
              </View>
            )}

            {/* Rules — different for Host vs Racer */}
            <View style={styles.confirmModalRulesBox}>
              {isHostMode ? (
                <>
                  <Text style={styles.confirmModalRulesTitle}>As the Host, You Agree To:</Text>
                  {isBattleRoyale ? (
                    // Warzone BR host: one private match, joined by game code.
                    // Host only = spectate via the CODCaster slot, no player
                    // slot consumed. Host & play (hostRacesActive) = the host
                    // drops in too and takes a normal player slot inside the
                    // cap (founder 2026-09-02).
                    <>
                      <Text style={styles.confirmModalRulesText}>Create the private match and submit the 6-character game code</Text>
                      {hostRacesActive ? (
                        <>
                          <Text style={styles.confirmModalRulesText}>You drop in too — enter from the event page to take your player slot</Text>
                          <Text style={styles.confirmModalRulesText}>Your seat counts inside the {gridSize}-player lobby cap</Text>
                        </>
                      ) : (
                        <>
                          <Text style={styles.confirmModalRulesText}>Spectate via the in-game CODCaster slot - hosts never play</Text>
                          <Text style={styles.confirmModalRulesText}>You don't take a player slot ({gridSize} players + you as host)</Text>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Text style={styles.confirmModalRulesText}>
                        {(isRacing || isShowdown)
                          ? 'Invite racers to your lobby via in-game invite'
                          : 'Set up and manage the lobby for the competing teams'}
                      </Text>
                      <Text style={styles.confirmModalRulesText}>
                        {hostRacesActive
                          ? (isShowdown
                            ? 'You race in the lobby you host — the full grid competes'
                            : (critsEnabled
                              ? 'You race in the heat you run — you pay the entry like every racer'
                              : 'You race in the heat you run and enter like every racer'))
                          : (isRacing || isShowdown)
                            ? 'Spectate only — hosts are not allowed to race'
                            : 'Spectate and cast only — hosts do not play'}
                      </Text>
                    </>
                  )}
                  <Text style={styles.confirmModalRulesText}>Ensure fair and smooth lobby management</Text>
                  {/* Manual-results games (every non-vision game): the host
                      runs the lobby, the players settle the result. */}
                  {usesManualResults && !isRacing && (
                    <Text style={[styles.confirmModalRulesText, { color: '#FCD000' }]}>
                      Matches settle by player reports — when play stops, players tap MATCH ENDED and report their results
                    </Text>
                  )}
                  {/* A hosting creator plays in NO bracket matches, so the
                      coordinator recording duty is theirs for every one. */}
                  {usesManualResults && isBracket && (
                    <Text style={[styles.confirmModalRulesText, { color: '#FCD000' }]}>
                      Record the results of the matches — as the creator, you record matches you are not playing in
                    </Text>
                  )}
                  {critsEnabled && (
                  <Text style={[styles.confirmModalRulesText, { color: '#4CAF50' }]}>You earn 20% of each lobby's Crit pool per round — up to 2x the more you host</Text>
                  )}
                </>
              ) : (
                <>
                  <Text style={styles.confirmModalRulesTitle}>As the {gp?.supportsHost && (isRacing || isShowdown) ? (isBattleRoyale ? 'Player' : 'Racer') : 'Creator'}, You Agree To:</Text>
                  {gp?.supportsHost && (isRacing || isShowdown) && (
                    <Text style={styles.confirmModalRulesText}>
                      {isBattleRoyale
                        ? 'The host posts a 6-character game code - join the private match in-game'
                        : 'The host will send you an in-game invite — check CritCash messages'}
                    </Text>
                  )}
                  <Text style={styles.confirmModalRulesText}>Stream your gameplay via CritCash</Text>
                  <Text style={styles.confirmModalRulesText}>Be ready to play at the scheduled time</Text>
                  <Text style={styles.confirmModalRulesText}>Follow all game rules and guidelines</Text>
                  <Text style={styles.confirmModalRulesText}>Act as a fair competitor</Text>
                  {/* Manual-results games (every non-vision game): the
                      creator agrees to the report flow up front — and on
                      brackets, to recording matches they're not playing in. */}
                  {usesManualResults && !isRacing && (
                    <Text style={[styles.confirmModalRulesText, { color: '#FCD000' }]}>
                      {isShowdown
                        ? 'Report your finish when the race ends — tap MATCH ENDED, then report your position'
                        : 'Report your result when the match ends — tap MATCH ENDED, then report'}
                    </Text>
                  )}
                  {usesManualResults && isBracket && (
                    <Text style={[styles.confirmModalRulesText, { color: '#FCD000' }]}>
                      As the creator, record the results of matches you are not playing in
                    </Text>
                  )}
                  {teamSize > 1 && (
                    <Text style={[styles.confirmModalRulesText, { color: '#9146FF' }]}>
                      All team members must stream and be ready
                    </Text>
                  )}
                </>
              )}
            </View>

            {/* TOS Agreement */}
            <View style={styles.confirmModalTosBox}>
              <Text style={styles.confirmModalTosText}>
                By creating this event, you agree to our{' '}
                <Text style={styles.confirmModalTosLink} onPress={() => openTermsOfService(navigation, { onBeforeNavigate: () => setShowCreateConfirmModal(false) })}>
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text style={styles.confirmModalTosLink} onPress={() => openTermsOfService(navigation, { onBeforeNavigate: () => setShowCreateConfirmModal(false) })}>
                  Competition Rules
                </Text>
                .
              </Text>
            </View>

            {/* Action Buttons */}
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity
                style={styles.confirmModalCancelBtn}
                onPress={() => setShowCreateConfirmModal(false)}
              >
                <Text style={styles.confirmModalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmModalConfirmBtn}
                onPress={confirmCreate}
                disabled={isCreating}
              >
                <Text style={styles.confirmModalConfirmText}>
                  {isCreating
                    ? 'Creating...'
                    : isAdmin
                      ? 'Create Event'
                      : isHostMode
                        ? 'Create as Host'
                        : !critsEnabled
                          ? 'Create Event'
                          : `Spend ${creatorTotalCrits} Crits & Create`}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* In-place membership upgrade funnel (members-only create rejection).
          Plan pick + embedded Stripe checkout inside the modal; the user's
          filled-out form stays mounted behind the scrim the whole time. */}
      <UpgradeMembershipModal
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgraded={() => setShowUpgradeModal(false)}
        onSeeAllPlans={() => navigation.navigate('Subscription')}
      />

      {/* In-place Crit-pack funnel (insufficient-Crits create rejection).
          Same philosophy: packs + embedded checkout in the modal, the form
          stays behind the scrim. Upgrade link -> Subscription screen (existing
          members change plans in-app there). */}
      <BuyCritsModal
        visible={!!critShortfall}
        shortfall={critShortfall}
        onClose={() => setCritShortfall(null)}
        onPurchased={() => setCritShortfall(null)}
        onUpgradePlan={() => navigation.navigate('Subscription')}
      />

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: COLORS.BG || '#0c0c0f' },
  scroll: { flex: 1 },
  container: { padding: 16, paddingBottom: 40 },
  h1: { color: COLORS.GOLD || '#ffd166', fontSize: 22, fontWeight: '700' },
  sub: { color: COLORS.MUTED || '#9aa0a6', marginTop: 4, fontSize: 12 },
  h2: { color: COLORS.GOLD || '#ffd166', fontSize: 16, fontWeight: '700', marginBottom: 8 },
  title: { color: '#fff', fontWeight: '700', marginTop: 4, fontSize: 14 },
  panel: {
    backgroundColor: COLORS.CARD || '#14161a',
    borderRadius: 12,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: COLORS.BORDER || '#1e2025',
  },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: '#1b1e24',
    borderWidth: 1,
    borderColor: '#2a2f36',
    marginRight: 8,
    marginBottom: 8,
  },
  pillActive: { backgroundColor: '#2a2f36', borderColor: COLORS.GOLD || '#ffd166' },
  pillText: { color: '#c9d1d9', fontSize: 13 },
  pillTextActive: { color: COLORS.GOLD || '#ffd166', fontWeight: '700' },
  pillDisabled: { opacity: 0.4 },
  pillTextDisabled: { color: '#666' },
  row: { flexDirection: 'row', gap: 12 },
  col: { flex: 1 },
  label: { color: '#c9d1d9', marginBottom: 6, fontWeight: '600', fontSize: 13 },
  input: {
    backgroundColor: '#0f1115',
    color: '#fff',
    borderWidth: 1,
    borderColor: '#2a2f36',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  note: { color: COLORS.MUTED || '#9aa0a6', fontSize: 12, marginTop: 4 },
  helperText: { 
    color: COLORS.MUTED || '#9aa0a6', 
    fontSize: 12, 
    marginTop: 8, 
    fontStyle: 'italic',
    lineHeight: 16,
  },
  preview: { borderColor: COLORS.GOLD || '#ffd166' },
  previewRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  previewLabel: { color: '#c9d1d9', fontSize: 13 },
  previewValue: { color: '#fff', fontWeight: '700', fontSize: 13 },
  previewLine: { color: '#fff', marginTop: 2, fontSize: 12 },
  cta: {
    marginTop: 16,
    backgroundColor: COLORS.GOLD || '#ffd166',
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
  },
  ctaDisabled: { opacity: 0.5 },
  ctaText: { color: '#000', fontWeight: '800', fontSize: 16 },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: COLORS.GOLD || '#ffd166',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: {
    backgroundColor: COLORS.GOLD || '#ffd166',
  },
  checkmark: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },

  // Game Availability Loading
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  loadingText: {
    color: '#888',
    marginLeft: 10,
    fontSize: 14,
  },

  // Pill wrapper for positioning badge
  pillWrapper: {
    position: 'relative',
  },

  // Disabled game styling for admins
  disabledGamePill: {
    opacity: 0.6,
    borderColor: '#E74C3C',
    borderWidth: 1,
  },

  // OFF badge for disabled games
  disabledBadge: {
    position: 'absolute',
    top: -6,
    right: 2,
    backgroundColor: '#E74C3C',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  disabledBadgeText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },

  // No games message
  noGamesMessage: {
    padding: 16,
    alignItems: 'center',
  },
  noGamesText: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
  },
  customGameInput: {
    backgroundColor: '#161616',
    borderColor: '#2a2a2a',
    borderWidth: 1,
    borderRadius: 8,
    color: '#EDEDED',
    fontSize: 14,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 10,
  },
  customGenreLabel: {
    color: '#888',
    fontSize: 12.5,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 6,
  },

  // Disabled game warning for admins
  disabledWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2a1a1a',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#E74C3C',
  },
  disabledWarningIcon: {
    fontSize: 16,
    fontWeight: '700',
    color: '#E74C3C',
    marginRight: 10,
  },
  disabledWarningTextWrap: {
    flex: 1,
  },
  disabledWarningTitle: {
    color: '#E74C3C',
    fontSize: 13,
    fontWeight: '600',
  },
  disabledWarningReason: {
    color: '#ff9999',
    fontSize: 12,
    marginTop: 2,
    fontStyle: 'italic',
  },

  // IGN Warning Banner
  ignWarningBanner: {
    backgroundColor: '#2a1a00',
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#FCD00050',
    overflow: 'hidden',
  },
  ignWarningContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
  },
  ignWarningIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  ignWarningText: {
    flex: 1,
  },
  ignWarningTitle: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },
  ignWarningSubtitle: {
    color: '#b39600',
    fontSize: 12,
    marginTop: 2,
  },
  ignWarningArrow: {
    color: '#FCD000',
    fontSize: 18,
    fontWeight: '600',
  },

  // IGN Set Confirmation
  ignSetBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a1f0a',
    borderRadius: 12,
    marginTop: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#00A86B30',
  },
  ignSetIcon: {
    color: '#00A86B',
    fontSize: 16,
    fontWeight: '700',
    marginRight: 10,
  },
  ignSetText: {
    flex: 1,
    color: '#888',
    fontSize: 13,
  },
  ignSetValue: {
    color: '#00A86B',
    fontWeight: '700',
  },
  ignEditLink: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '600',
  },
    streamKeyWarningBanner: {
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#9146FF50',
    overflow: 'hidden',
  },
  
  // Shared warning styles
  warningContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
  },
  warningIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  warningText: {
    flex: 1,
  },
  warningTitle: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },
  warningSubtitle: {
    color: '#888',
    fontSize: 12,
    marginTop: 2,
  },
  warningArrow: {
    color: '#FCD000',
    fontSize: 18,
    fontWeight: '600',
  },
  
  // Eligible banner
  eligibleBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a1f0a',
    borderRadius: 12,
    marginTop: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#00A86B30',
  },
  eligibleIcon: {
    color: '#00A86B',
    fontSize: 16,
    fontWeight: '700',
    marginRight: 10,
  },
  eligibleText: {
    flex: 1,
    color: '#888',
    fontSize: 13,
  },
  eligibleValue: {
    color: '#00A86B',
    fontWeight: '700',
  },
  
  // Confirmation Modal Styles
  confirmModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModalContent: {
    backgroundColor: '#1a1e2e',
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    maxHeight: '90%',
    borderWidth: 1,
    borderColor: '#2a3040',
  },
  confirmModalTitle: {
    color: COLORS.GOLD || '#FCD000',
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 16,
  },
  confirmModalEventBox: {
    backgroundColor: '#0f1220',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#1a2040',
    alignItems: 'center',
  },
  confirmModalEventName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 4,
  },
  confirmModalEventGame: {
    color: '#8a8fa8',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 4,
  },
  confirmModalEventDetails: {
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
  },
  confirmModalFeeBox: {
    backgroundColor: 'rgba(252, 208, 0, 0.1)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(252, 208, 0, 0.2)',
    alignItems: 'center',
  },
  confirmModalFeeLabel: {
    color: COLORS.GOLD || '#FCD000',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  confirmModalFeeAmount: {
    color: COLORS.GOLD || '#FCD000',
    fontSize: 28,
    fontWeight: '800',
  },
  confirmModalFeeBreakdown: {
    color: '#AAAAAA',
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
  confirmModalPaymentBox: {
    backgroundColor: 'rgba(233, 30, 99, 0.1)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(233, 30, 99, 0.2)',
  },
  confirmModalPaymentTitle: {
    color: '#E91E63',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 10,
  },
  confirmModalPaymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  confirmModalPaymentLabel: {
    color: '#ccc',
    fontSize: 13,
  },
  confirmModalPaymentValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  confirmModalPaymentNote: {
    color: '#888',
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },
  confirmModalHostEarnings: {
    marginTop: 12,
    backgroundColor: '#0f1220',
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
  },
  confirmModalHostEarningsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  confirmModalHostEarningsTitle: {
    color: '#4CAF50',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  confirmModalHostEarningsAmount: {
    color: '#4CAF50',
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 2,
  },
  confirmModalHostEarningsBreakdown: {
    width: '100%',
    marginTop: 8,
    gap: 4,
  },
  confirmModalHostEarningsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  confirmModalHostEarningsLabel: {
    color: '#888',
    fontSize: 11,
  },
  confirmModalHostEarningsValue: {
    color: '#ccc',
    fontSize: 11,
    fontWeight: '700',
  },
  confirmModalHostEarningsNote: {
    color: '#666',
    fontSize: 10,
    textAlign: 'center',
    marginTop: 6,
  },
  confirmModalRoleBox: {
    marginBottom: 12,
  },
  confirmModalRoleLabel: {
    color: '#9aa0b4',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  confirmModalRoleRow: {
    flexDirection: 'row',
    gap: 8,
  },
  confirmModalRolePill: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#1a1c2e',
    borderWidth: 1,
    borderColor: '#333',
    alignItems: 'center',
  },
  confirmModalRolePillActive: {
    backgroundColor: '#3a3200',
    borderColor: '#FCD000',
  },
  confirmModalRolePillText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '700',
  },
  confirmModalRolePillTextActive: {
    color: '#FCD000',
  },
  confirmModalRulesBox: {
    backgroundColor: '#0f1220',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  confirmModalRulesTitle: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 8,
  },
  confirmModalRulesText: {
    color: '#9aa0b4',
    fontSize: 12,
    lineHeight: 18,
    marginBottom: 2,
  },
  confirmModalTosBox: {
    marginBottom: 20,
  },
  confirmModalTosText: {
    color: '#888',
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 16,
  },
  confirmModalTosLink: {
    color: COLORS.GOLD || '#FCD000',
    textDecorationLine: 'underline',
  },
  confirmModalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmModalCancelBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#3a4060',
    alignItems: 'center',
  },
  confirmModalCancelText: {
    color: '#9aa0b4',
    fontSize: 15,
    fontWeight: '600',
  },
  confirmModalConfirmBtn: {
    flex: 2,
    padding: 14,
    borderRadius: 10,
    backgroundColor: COLORS.GOLD || '#FCD000',
    alignItems: 'center',
  },
  confirmModalConfirmText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '700',
  },

  // Team Info Styles
  teamInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#1a1a2e',
    borderRadius: 10,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#9146FF30',
  },
  teamInfoTitle: {
    color: '#9146FF',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  teamInfoText: {
    color: '#8a8fa8',
    fontSize: 12,
    lineHeight: 18,
  },
  lockedTeamBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(252, 208, 0, 0.15)',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  lockedTeamText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '700',
  },
  
  // Team Summary in Confirmation Modal
  teamSummaryBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#9146FF30',
  },
  teamSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  teamSummaryLabel: {
    color: '#8a8fa8',
    fontSize: 12,
  },
  teamSummaryValue: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  teamSummaryHighlight: {
    color: '#9146FF',
    fontWeight: '700',
  },
});